// ../../packages/client/src/operators.ts
var PRED = Symbol("rindle.pred");
function isPred(x) {
  return typeof x === "object" && x !== null && x[PRED] === true;
}
function fieldCondition(field, arg) {
  const op = isPred(arg) ? arg.op : "=";
  const value = isPred(arg) ? arg.value : arg;
  return {
    type: "simple",
    op,
    left: { type: "column", name: field },
    right: { type: "literal", value }
  };
}

// ../../packages/client/src/schema.ts
var SCHEMA = Symbol("rindle.schema");
function isLocalTable(schema, table) {
  return Boolean(schema.tables[table]?.local);
}
var RELATIONSHIP_BRAND = Symbol("rindle.relationship");
function isRelationship(v) {
  return typeof v === "object" && v !== null && v[RELATIONSHIP_BRAND] === true;
}

// ../../packages/client/src/key.ts
function stableKey(value, seen = /* @__PURE__ */ new WeakSet()) {
  if (value === void 0) return '{"$undefined":true}';
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (seen.has(value)) throw new TypeError("Rindle query keys must be acyclic JSON values");
  seen.add(value);
  if (Array.isArray(value)) {
    const out2 = `[${value.map((v) => stableKey(v, seen)).join(",")}]`;
    seen.delete(value);
    return out2;
  }
  const obj = value;
  const out = `{${Object.keys(obj).sort().map((k) => `${JSON.stringify(k)}:${stableKey(obj[k], seen)}`).join(",")}}`;
  seen.delete(value);
  return out;
}

// ../../packages/client/src/query.ts
var STAMP_NAMED_QUERY = Symbol("rindle.stampNamedQuery");
var FRAGMENT_BRAND = Symbol("rindle.fragment");
var LOCAL_FRAGMENT_REF_BRAND = Symbol("rindle.localFragmentRef");
var FRAGMENT_REL_BRAND = Symbol("rindle.fragmentRelationship");
function isFragment(v) {
  return typeof v === "function" && v[FRAGMENT_BRAND] === true;
}
function markFragmentRelationship(rel) {
  Object.defineProperty(rel, FRAGMENT_REL_BRAND, { value: true });
  return rel;
}
function isFragmentRelationship(rel) {
  return rel[FRAGMENT_REL_BRAND] === true;
}
function emptyState(table) {
  return { table, wheres: [], orderBy: [], related: [], one: false, groupBy: [] };
}
function compile(s) {
  const ast = { table: s.table };
  if (s.alias !== void 0) ast.alias = s.alias;
  if (s.wheres.length === 1) ast.where = s.wheres[0];
  else if (s.wheres.length > 1) ast.where = { type: "and", conditions: s.wheres };
  if (s.related.length > 0) ast.related = s.related;
  if (s.start !== void 0) ast.start = s.start;
  if (s.orderBy.length > 0) ast.orderBy = s.orderBy;
  if (s.limit !== void 0) ast.limit = s.limit;
  if (s.one) ast.one = true;
  if (s.select && s.select.length > 0) ast.select = s.select;
  if (s.aggregate !== void 0) ast.aggregate = s.aggregate;
  if (s.groupBy.length > 0) ast.groupBy = s.groupBy;
  if (s.having !== void 0) ast.having = s.having;
  if ((ast.groupBy !== void 0 || ast.having !== void 0) && ast.aggregate === void 0) {
    throw new Error(
      "groupBy()/having() require count(): a top-level GROUP BY / HAVING is only honored on an aggregate query (REDUCE-DESIGN.md \xA78) \u2014 add .count()."
    );
  }
  return ast;
}
function astTables(ast) {
  const out = /* @__PURE__ */ new Set();
  const walk = (v) => {
    if (Array.isArray(v)) {
      for (const x of v) walk(x);
    } else if (v && typeof v === "object") {
      const o = v;
      if (typeof o.table === "string") out.add(o.table);
      for (const k of Object.keys(o)) walk(o[k]);
    }
  };
  walk(ast);
  return out;
}
function assertNoLocalTables(ast, schema) {
  for (const t of astTables(ast)) {
    if (isLocalTable(schema, t)) {
      throw new Error(
        `local-only table "${t}" may not be used in a server/named query \u2014 it was reached through a relationship/subquery; build it from store.query (the local builder) instead (201-LOCAL-ONLY-TABLES-DESIGN.md \xA75 / Q1 / E3).`
      );
    }
  }
}
function childAst(child, build) {
  const cm = child[SCHEMA];
  let cq = makeQuery(cm, emptyState(cm.name));
  if (build) cq = build(cq);
  return cq.ast();
}
function composeCorrelatedBuild(build, edge) {
  if (edge === void 0) return build;
  if (!isFragment(build)) {
    throw new Error("sub(): an edge callback is only supported when the child build is a Fragment.");
  }
  return (q) => edge(build(q));
}
function resolveCorrelated(a, b, c, d) {
  if (isRelationship(a)) {
    return {
      child: a.child,
      corr: { parent: [...a.correlation.parent], child: [...a.correlation.child] },
      build: composeCorrelatedBuild(
        b,
        c
      ),
      fragment: isFragment(b)
    };
  }
  return {
    child: a,
    corr: b,
    build: composeCorrelatedBuild(
      c,
      d
    ),
    fragment: isFragment(c)
  };
}
function uniqSort(cols) {
  return [...new Set(cols)].sort();
}
function mergeNestedSelect(a, b) {
  if (a === void 0 || a.length === 0 || b === void 0 || b.length === 0) return void 0;
  return uniqSort([...a, ...b]);
}
function mergeRootSelect(baseSel, fragSel) {
  const union = uniqSort([...baseSel ?? [], ...fragSel ?? []]);
  return union.length === 0 ? void 0 : union;
}
function aliasOf(csq) {
  return csq.subquery.alias ?? "";
}
function edgeShapeKey(csq) {
  const sq = csq.subquery;
  return stableKey({
    correlation: csq.correlation,
    system: csq.system,
    table: sq.table,
    where: sq.where,
    orderBy: sq.orderBy,
    limit: sq.limit,
    start: sq.start,
    one: sq.one,
    aggregate: sq.aggregate,
    aggregatePrecomputed: sq.aggregatePrecomputed
  });
}
function mergeSubqueryAst(base, frag) {
  const out = { ...base };
  const select = mergeNestedSelect(base.select, frag.select);
  if (select === void 0) delete out.select;
  else out.select = select;
  const related = mergeRelatedLists(base.related ?? [], frag.related ?? []);
  if (related.length === 0) delete out.related;
  else out.related = related;
  return out;
}
function mergeRelatedLists(base, add) {
  const byAlias = /* @__PURE__ */ new Map();
  for (const csq of base) byAlias.set(aliasOf(csq), csq);
  for (const csq of add) {
    const alias = aliasOf(csq);
    const existing = byAlias.get(alias);
    if (existing === void 0) {
      byAlias.set(alias, csq);
      continue;
    }
    if (edgeShapeKey(existing) !== edgeShapeKey(csq)) {
      throw new Error(
        `include(): conflicting definitions for relationship "${alias}" \u2014 two fragments spread the same alias with different correlation/table/where/orderBy/limit. Give them distinct aliases or align them (FRAGMENT-COMPOSITION-DESIGN \xA710.2).`
      );
    }
    const merged = { ...existing, subquery: mergeSubqueryAst(existing.subquery, csq.subquery) };
    byAlias.set(alias, isFragmentRelationship(existing) || isFragmentRelationship(csq) ? markFragmentRelationship(merged) : merged);
  }
  return [...byAlias.values()].sort((a, b) => {
    const x = aliasOf(a);
    const y = aliasOf(b);
    return x < y ? -1 : x > y ? 1 : 0;
  });
}
function mergeIncludedFragment(s, frag) {
  if (frag.where !== void 0) {
    throw new Error(
      "include(): a fragment may not add a root `where` \u2014 only the rooting query filters (FRAGMENT-COMPOSITION-DESIGN \xA710.3)."
    );
  }
  if (frag.orderBy !== void 0 || frag.limit !== void 0 || frag.start !== void 0 || frag.one) {
    throw new Error(
      "include(): a fragment may not set a root `orderBy`/`limit`/`start`/`one` \u2014 it contributes only `select` and nested relationships (those define the node's window, which is the rooting query's job)."
    );
  }
  const select = mergeRootSelect(s.select, frag.select);
  const next = { ...s, related: mergeRelatedLists(s.related, frag.related ?? []) };
  if (select === void 0) delete next.select;
  else next.select = select;
  return next;
}
function makeQuery(meta, s, onMat, named, guardAst) {
  const next = (patch) => makeQuery(meta, { ...s, ...patch }, onMat, named, guardAst);
  const applyField = (field, arg) => next({ wheres: [...s.wheres, fieldCondition(field, arg)] });
  let proxy;
  const base = {
    name: named?.name,
    args: named?.args,
    where: (cond) => next({ wheres: [...s.wheres, cond] }),
    orderBy: (col, dir) => next({ orderBy: [...s.orderBy, [col, dir]] }),
    select: (...cols) => next({ select: [...s.select ?? [], ...cols] }),
    limit: (n) => next({ limit: n }),
    start: (cursor, opts) => next({ start: { row: { ...cursor }, exclusive: opts?.exclusive ?? false } }),
    one: () => next({ one: true }),
    include: (fragment) => {
      const fragTable = fragment.table;
      if (fragTable[SCHEMA].name !== s.table) {
        throw new Error(
          `include(): the fragment is over "${fragTable[SCHEMA].name}" but this query is over "${s.table}". include() merges a fragment into the SAME table \u2014 use sub() to nest a different table.`
        );
      }
      const fragAst = childAst(fragTable, fragment);
      return makeQuery(meta, mergeIncludedFragment(s, fragAst), onMat, named, guardAst);
    },
    sub: (alias, childOrRel, b, c, d) => {
      const { child, corr, build, fragment } = resolveCorrelated(childOrRel, b, c, d);
      const sub = childAst(child, build);
      sub.alias = alias;
      const csq = {
        correlation: { parentField: corr.parent, childField: corr.child },
        subquery: sub
      };
      return next({ related: [...s.related, fragment ? markFragmentRelationship(csq) : csq] });
    },
    countAs: (alias, childOrRel, b, c) => {
      const { child, corr, build } = resolveCorrelated(childOrRel, b, c);
      const sub = childAst(child, build);
      sub.alias = alias;
      sub.aggregate = "count";
      const csq = {
        correlation: { parentField: corr.parent, childField: corr.child },
        subquery: sub
      };
      return next({ related: [...s.related, csq] });
    },
    // Top-level aggregate (REDUCE-DESIGN §8): `count` reshapes this query into a `count(*)`;
    // `groupBy` partitions it; `having` filters the post-aggregation rows. `having`'s callback gets
    // a field binder over the aggregate's output columns (group cols + the synthetic `count`), so a
    // predicate can name `count` — which lives on no base table — exactly like `where.<field>(…)`.
    count: () => next({ aggregate: "count" }),
    groupBy: (col) => next({ groupBy: [...s.groupBy, col] }),
    having: (a, op, val) => {
      if (typeof a === "function") {
        const build = a;
        const h = new Proxy({}, {
          get: (_t, prop) => typeof prop === "string" ? (arg) => fieldCondition(prop, arg) : void 0
        });
        return next({ having: build(h) });
      }
      const alias = a;
      const display = s.related.find(
        (r) => r.subquery.alias === alias && r.subquery.aggregate === "count"
      );
      if (!display) {
        throw new Error(
          `having("${alias}", \u2026): this query has no countAs("${alias}", \u2026) relationship to filter on \u2014 attach the child count aggregate first.`
        );
      }
      const gate = {
        correlation: display.correlation,
        subquery: {
          ...display.subquery,
          alias: `__having_${alias}`,
          having: {
            type: "simple",
            op,
            left: { type: "column", name: "count" },
            right: { type: "literal", value: val }
          }
        }
      };
      return next({ wheres: [...s.wheres, { type: "correlatedSubquery", op: "EXISTS", related: gate }] });
    },
    ast: () => {
      const a = compile(s);
      guardAst?.(a);
      return a;
    },
    materialize: () => {
      if (!onMat) throw new Error("materialize() requires a Store \u2014 use store.query.<table>");
      return onMat(proxy);
    }
  };
  const whereProxy = new Proxy(base.where, {
    get(_t, prop) {
      if (typeof prop === "string") return (arg) => applyField(prop, arg);
      return void 0;
    }
  });
  proxy = new Proxy(base, {
    get(target, prop) {
      if (prop === "where") return whereProxy;
      if (prop === STAMP_NAMED_QUERY)
        return (name, args) => makeQuery(meta, s, onMat, { name, args }, guardAst);
      if (typeof prop === "string" && prop.length > 5 && prop.startsWith("where")) {
        const field = prop[5].toLowerCase() + prop.slice(6);
        return (arg) => applyField(field, arg);
      }
      return target[prop];
    }
  });
  return proxy;
}
function queries(schema, onMaterialize, opts) {
  const includeLocal = opts?.includeLocal ?? false;
  return new Proxy({}, {
    get(_t, prop) {
      if (typeof prop !== "string") return void 0;
      if (prop[0] === "$" || prop === "then" || prop === "toJSON") return void 0;
      const meta = schema.tables[prop];
      if (!meta) throw new Error(`unknown table: ${prop}`);
      if (meta.local && !includeLocal) {
        throw new Error(
          `local-only table "${prop}" may not be used in a server/named query \u2014 build it from store.query (the local builder) and materialize it ad-hoc (201-LOCAL-ONLY-TABLES-DESIGN.md \xA75 / Q1).`
        );
      }
      const guardAst = includeLocal ? void 0 : (ast) => assertNoLocalTables(ast, schema);
      return makeQuery(meta, emptyState(meta.name), onMaterialize, void 0, guardAst);
    }
  });
}
function newQueryBuilder(schema) {
  return queries(schema);
}

// ../../packages/client/src/compare.ts
var enc = new TextEncoder();
var COMPARATOR_VERSION = 2;
var SIGN = 0x8000000000000000n;
var ALL = 0xffffffffffffffffn;
function orderedKey(x) {
  const dv = new DataView(new ArrayBuffer(8));
  dv.setFloat64(0, x);
  const bits = dv.getBigUint64(0);
  return bits & SIGN ? bits ^ ALL : bits | SIGN;
}
function compareNumber(a, b) {
  const ka = orderedKey(a);
  const kb = orderedKey(b);
  return ka < kb ? -1 : ka > kb ? 1 : 0;
}
function compareString(a, b) {
  const ba = enc.encode(a);
  const bb = enc.encode(b);
  const n = Math.min(ba.length, bb.length);
  for (let i = 0; i < n; i++) {
    if (ba[i] !== bb[i]) return ba[i] < bb[i] ? -1 : 1;
  }
  return ba.length === bb.length ? 0 : ba.length < bb.length ? -1 : 1;
}
function compareValue(a, b) {
  const an = a === null || a === void 0;
  const bn = b === null || b === void 0;
  if (an && bn) return 0;
  if (an) return -1;
  if (bn) return 1;
  switch (typeof a) {
    case "number":
      return compareNumber(a, b);
    case "boolean":
      return a === b ? 0 : a ? 1 : -1;
    case "string":
      return compareString(a, b);
    default:
      return compareString(JSON.stringify(a), JSON.stringify(b));
  }
}
function compareRows(a, b, sort) {
  for (const [col, asc] of sort) {
    const c = compareValue(a[col], b[col]);
    if (c !== 0) return asc ? c : -c;
  }
  return 0;
}

// ../../packages/client/src/view.ts
var SingularView = class {
  inner;
  constructor(inner) {
    this.inner = inner;
  }
  get data() {
    return this.inner.data[0] ?? null;
  }
  get qid() {
    return this.inner.qid;
  }
  get schema() {
    return this.inner.schema;
  }
  get resultType() {
    return this.inner.resultType;
  }
  subscribe(listener) {
    return this.inner.subscribe((d) => listener(d[0] ?? null));
  }
  onChanges(listener) {
    return this.inner.onChanges(listener);
  }
  destroy() {
    this.inner.destroy();
  }
};
function binarySearch(list, row, sort) {
  let lo = 0;
  let hi = list.length;
  while (lo < hi) {
    const mid = lo + hi >>> 1;
    const c = compareRows(list[mid].row, row, sort);
    if (c < 0) lo = mid + 1;
    else if (c > 0) hi = mid;
    else return { found: true, index: mid };
  }
  return { found: false, index: lo };
}
function buildNode(wnode, schema) {
  const rels = schema.relationships.map(() => []);
  for (const { rel, children } of wnode.rels) {
    const child = schema.relationships[rel]?.child;
    if (!child) continue;
    const built = [];
    for (const c of children) {
      const at = binarySearch(built, c.row, child.sort);
      if (at.found) built[at.index].rc += 1;
      else built.splice(at.index, 0, buildNode(c, child));
    }
    rels[rel] = built;
  }
  return { row: wnode.row, rc: 1, rels, out: null };
}
function cloneNode(n) {
  return { row: n.row.slice(), rc: n.rc, rels: n.rels.map((r) => r.map(cloneNode)), out: null };
}
function toWireNode(node, schema) {
  const rels = [];
  for (const rel of schema.relationships) {
    if (rel.child === null) continue;
    const childList = node.rels[rel.slot] ?? [];
    if (childList.length === 0) continue;
    rels.push({ rel: rel.slot, children: childList.map((c) => toWireNode(c, rel.child)) });
  }
  return { row: node.row, rels };
}
function rowsEqual(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i] && !Object.is(a[i], b[i])) return false;
  }
  return true;
}
function keyRow(row) {
  return JSON.stringify(
    row,
    (_k, v) => typeof v === "number" && (!Number.isFinite(v) || Object.is(v, -0)) ? [" num", Object.is(v, -0) ? "-0" : String(v)] : v
  );
}
function opRowKey(path, row) {
  let k = "";
  for (const seg of path) k += seg.rel + ":" + keyRow(seg.parentRow) + "/";
  return k + "|" + keyRow(row);
}
function levelSchemaAt(schema, path) {
  let level = schema;
  for (const seg of path) {
    const child = level.relationships[seg.rel]?.child;
    if (!child) return null;
    level = child;
  }
  return level;
}
function nodesEqual(a, b) {
  if (a.rc !== b.rc || !rowsEqual(a.row, b.row) || a.rels.length !== b.rels.length) return false;
  for (let s = 0; s < a.rels.length; s++) {
    const ra = a.rels[s];
    const rb = b.rels[s];
    if (ra.length !== rb.length) return false;
    for (let i = 0; i < ra.length; i++) if (!nodesEqual(ra[i], rb[i])) return false;
  }
  return true;
}
function netChanges(changes, schema) {
  if (changes.length < 2) return changes;
  const alive = new Array(changes.length).fill(true);
  const removes = /* @__PURE__ */ new Map();
  const adds = /* @__PURE__ */ new Map();
  const edits = /* @__PURE__ */ new Map();
  const subCache = /* @__PURE__ */ new Map();
  const subtreeOf = (i) => {
    let s = subCache.get(i);
    if (s !== void 0) return s;
    const { path, op } = changes[i];
    const level = levelSchemaAt(schema, path);
    const wnode = op.tag === "add" ? op.node : op.tag === "remove" ? op.node : void 0;
    s = level && wnode ? buildNode(wnode, level) : null;
    subCache.set(i, s);
    return s;
  };
  const takeInverse = (m, k, i) => {
    const arr = m.get(k);
    if (!arr || arr.length === 0) return void 0;
    const mine = subtreeOf(i);
    if (mine === null) return void 0;
    for (let x = arr.length - 1; x >= 0; x--) {
      const theirs = subtreeOf(arr[x]);
      if (theirs !== null && nodesEqual(mine, theirs)) {
        const j = arr[x];
        arr.splice(x, 1);
        return j;
      }
    }
    return void 0;
  };
  const take = (m, k) => {
    const arr = m.get(k);
    return arr && arr.length ? arr.pop() : void 0;
  };
  const put = (m, k, i) => {
    const arr = m.get(k);
    if (arr) arr.push(i);
    else m.set(k, [i]);
  };
  let cancelled = false;
  for (let i = 0; i < changes.length; i++) {
    const { path, op } = changes[i];
    if (op.tag === "remove") {
      const k = opRowKey(path, op.row);
      const j = takeInverse(adds, k, i);
      if (j !== void 0) {
        alive[i] = alive[j] = false;
        cancelled = true;
      } else put(removes, k, i);
    } else if (op.tag === "add") {
      const k = opRowKey(path, op.node.row);
      const j = takeInverse(removes, k, i);
      if (j !== void 0) {
        alive[i] = alive[j] = false;
        cancelled = true;
      } else put(adds, k, i);
    } else {
      const pk = opRowKey(path, []);
      const oldS = keyRow(op.old);
      const newS = keyRow(op.new);
      const j = take(edits, pk + "\0" + newS + "\0" + oldS);
      if (j !== void 0) {
        alive[i] = alive[j] = false;
        cancelled = true;
      } else put(edits, pk + "\0" + oldS + "\0" + newS, i);
    }
  }
  if (!cancelled) return changes;
  const out = [];
  for (let i = 0; i < changes.length; i++) if (alive[i]) out.push(changes[i]);
  return out;
}
var FlatFoldError = class extends Error {
  /** The engine query id of the view that could not fold (`ArrayView.qid`). */
  qid;
  /** The change that could not be applied — its `path` locators and its `op`. */
  change;
  /** How many path hops the descent completed before failing (`change.path.length` ⇒ the op's
   *  own level; less ⇒ a parent locator went missing on the way down). */
  depth;
  /** The level the failing search ran against: what the view actually held there. */
  level;
  constructor(what, qid, change, depth, list, schema) {
    super(`flat ArrayView: ${what}`);
    this.name = "FlatFoldError";
    this.qid = qid;
    this.change = change;
    this.depth = depth;
    const cap = 64;
    this.level = {
      columns: schema.columns.slice(),
      sort: schema.sort.slice(),
      rows: list.slice(0, cap).map((n) => n.row.slice()),
      rc: list.slice(0, cap).map((n) => n.rc)
    };
  }
};
var EMPTY = Object.freeze([]);
var FlatArrayView = class {
  // The engine query id (Store-assigned, 1:1 with this view), exposed read-only via {@link qid}.
  // `0` ⇒ unbound (a bare view never registered with a Store); the Store passes it at construction.
  _qid;
  // `null` ⇒ PENDING (no schema yet): a remote backend's `hello` arrives async, so the view
  // exists (and reads as `[]`) before its schema lands. Set by `reset` (first hello / re-hydrate).
  // Exposed read-only via the {@link schema} getter (the position→name source for `resolveChange`).
  _schema;
  types;
  // SSR first-paint seed (SSR-DESIGN.md §6): pre-projected rows installed by `seed`, returned by
  // `data` until the maintained tree's first live SNAPSHOT lands (the Store calls `retireSeed` then).
  // It deliberately SURVIVES `reset` (the `hello`), so a seeded view bridges the `hello`→snapshot gap
  // instead of flashing empty in between (a `hello` sets the schema but its data arrives one round-trip
  // later, on the snapshot). `null` ⇒ no seed.
  seeded = null;
  top = [];
  dirty = true;
  cached = null;
  // `complete` by default: a backend with no server lifecycle (the in-process engine) never pushes
  // a resultType, so its views read as authoritative. The Store overrides this for backends that do
  // (the optimistic backend: `unknown` until hydrated, etc.).
  rt = "complete";
  listeners = /* @__PURE__ */ new Set();
  // The per-view CHANGE stream ({@link onChanges}) — the diff a narrator rides, distinct from the
  // data channel above. Empty until a narrator attaches: an idle view pays one `.size` check per fold.
  changeListeners = /* @__PURE__ */ new Set();
  // Changes folded but not yet delivered to `changeListeners` — buffered while the Store defers a
  // commit (mirrors the deferred `notify`), then netted + flushed at the commit boundary. Segmented
  // BY PHASE (not one last-writer-wins phase scalar): a bracket that folds both a `snapshot` and a
  // `batch` into this view keeps them apart, each netted and delivered under its OWN phase, so a
  // snapshot is never mislabeled `batch` (nor a batch dropped as `snapshot`). Consecutive same-phase
  // folds coalesce into one segment so a whole commit's batch nets together.
  pendingSegments = [];
  constructor(schema, types, qid = 0) {
    this._schema = schema ?? null;
    this.types = types;
    this._qid = qid;
  }
  get qid() {
    return this._qid;
  }
  get schema() {
    return this._schema;
  }
  get resultType() {
    return this.rt;
  }
  /** Set the query's lifecycle state (the Store routes the backend's per-query signal here).
   *  Notifies subscribers on a change so a status-bound listener (React `useQueryStatus`) re-reads,
   *  WITHOUT re-projecting data (it is unchanged). */
  setResultType(rt) {
    if (this.rt === rt) return;
    this.rt = rt;
    for (const l of this.listeners) l(this.data);
  }
  /** (Re)bind to a schema and clear the tree IN PLACE. The first `hello` (pending → ready)
   *  and a re-hydrate (gap → new epoch — FLAT-CHANGES-DESIGN.md §2.3) both go through here, so
   *  the caller's view reference and its subscribers survive a re-subscribe. Does NOT notify —
   *  the snapshot that follows (`applyChanges`) does, avoiding an empty-then-filled flicker.
   *  KEEPS any SSR `seeded` rows: they are retired only when the first live snapshot lands
   *  ({@link retireSeed}, driven by the Store), so `data` shows the seed — not an empty tree —
   *  across the whole `hello`→first-`snapshot` gap. */
  reset(schema, types) {
    this._schema = schema;
    this.types = types;
    this.top = [];
    this.cached = null;
    this.dirty = true;
    this.pendingSegments = [];
  }
  /** Install a pre-projected SSR first-paint snapshot (SSR-DESIGN.md §6). The rows are already
   *  in result shape (json columns parsed, relationships nested), so a view with no live backend
   *  (the server one-shot Store) reads them directly, and a browser view shows them until its
   *  first live snapshot lands ({@link retireSeed}). Does NOT notify — it is set at materialize
   *  time, before any subscriber, and the live snapshot that follows notifies. */
  seed(rows) {
    this.seeded = rows;
  }
  /** Retire the SSR first-paint seed — the Store calls this as it folds the maintained tree's first
   *  live snapshot, so `data` switches from the seed to the live tree with no empty gap between them
   *  (the seed deliberately survived the earlier `reset`/`hello`). Idempotent. Does NOT notify — the
   *  snapshot fold it accompanies does; BUT when that fold is empty (a 0-row result, or rows already
   *  in `top`) it notifies nothing, so the Store forces a {@link notify} on the strength of the `true`
   *  return here — else the view reads the live tree yet never re-renders (a frozen seed). Returns
   *  whether a live seed was actually cleared (so the Store knows a forced notify is owed). */
  retireSeed() {
    if (this.seeded === null) return false;
    this.seeded = null;
    return true;
  }
  /** Apply a batch (the hydrate snapshot or one transaction's events) in order, then
   *  notify subscribers once. Order is significant (FLAT-CHANGES-DESIGN.md §5.4). A no-op
   *  while pending (changes never precede the `hello` that resets the schema).
   *
   *  `enrichRemoves` ⇒ before a removed node is dropped, reconstruct its full subtree and attach it
   *  to the `remove` op's `node` (in place, so the same event object the Store fans out to its
   *  change subscribers carries it). Off by default — paid only when a consumer asked for it, and
   *  only on a real eviction (an rc-decrement that keeps the row leaves `node` absent). A view with
   *  an attached {@link onChanges} listener ALSO enriches (per-view, no global opt-in), so a
   *  narrator can resolve a removed row's subs whether or not the store-global counter is set.
   *
   *  `deferNotify` ⇒ fold but do NOT notify; the caller is responsible for calling {@link flush}
   *  later. The Store uses this to fold every view in one commit before notifying any subscriber
   *  (cross-view-atomic notification — `Store.onCommitBoundary`); standalone use leaves it off, so
   *  a bare view still fires its subscribers after each applied batch.
   *
   *  `phase` tags the {@link onChanges} delivery (`snapshot` for the hydrate, `batch` otherwise); it
   *  does not affect the fold. Returns whether the batch changed the view (so a deferring caller
   *  knows it must be flushed). */
  applyChanges(events, enrichRemoves = false, deferNotify = false, phase = "batch") {
    if (this._schema === null) return false;
    const enrich = enrichRemoves || this.changeListeners.size > 0;
    const applied = [];
    for (const e of events) {
      if (this.applyAt(this.top, this._schema, e, 0, enrich)) applied.push(e);
    }
    if (applied.length === 0) return false;
    this.dirty = true;
    if (deferNotify) {
      if (this.changeListeners.size > 0) {
        const segs = this.pendingSegments;
        const last = segs.length > 0 ? segs[segs.length - 1] : void 0;
        if (last && last.phase === phase) for (const e of applied) last.changes.push(e);
        else segs.push({ phase, changes: applied.slice() });
      }
      return true;
    }
    let firstError;
    let hasError = false;
    const note = (e) => {
      if (!hasError) {
        hasError = true;
        firstError = e;
      }
    };
    try {
      this.notify();
    } catch (e) {
      note(e);
    }
    this.deliverChanges(applied, phase, note);
    if (hasError) throw firstError;
    return true;
  }
  /** Notify subscribers with the current data. The deferred half of {@link applyChanges} (when
   *  `deferNotify` was set): the Store calls this at the commit-notify barrier — after every view
   *  touched by the same commit has folded — so a subscriber that re-reads a sibling view inside
   *  its callback observes post-commit data, never a torn mid-commit state. */
  flush() {
    const segs = this.pendingSegments;
    this.pendingSegments = [];
    let firstError;
    let hasError = false;
    const note = (e) => {
      if (!hasError) {
        hasError = true;
        firstError = e;
      }
    };
    try {
      this.notify();
    } catch (e) {
      note(e);
    }
    for (const seg of segs) this.deliverChanges(seg.changes, seg.phase, note);
    if (hasError) throw firstError;
  }
  get data() {
    if (this.seeded !== null) return this.seeded;
    if (this._schema === null) return EMPTY;
    if (!this.dirty && this.cached !== null) return this.cached;
    this.cached = this.top.map((n) => this.project(n, this._schema, this.types));
    this.dirty = false;
    return this.cached;
  }
  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.data);
    return () => {
      this.listeners.delete(listener);
    };
  }
  onChanges(listener) {
    this.changeListeners.add(listener);
    return () => {
      this.changeListeners.delete(listener);
    };
  }
  /** Net the folded batch and hand the survivors to the change listeners, AFTER the data `notify`
   *  (the order `Store.subscribeChanges` consumers already observe). A batch that nets to nothing —
   *  a correctly predicted rebase — makes no call, so a narrator sees only real change. Each listener
   *  is isolated: a throwing one reports to `note` (the caller re-raises the first) and the rest still
   *  run, so one bad narration template never starves a sibling listener nor corrupts the view. */
  deliverChanges(events, phase, note) {
    if (this.changeListeners.size === 0) return;
    const schema = this._schema;
    const net = netChanges(events, schema);
    if (net.length === 0) return;
    for (const l of this.changeListeners) {
      try {
        l(net, phase, schema);
      } catch (e) {
        note(e);
      }
    }
  }
  destroy() {
    this.listeners.clear();
    this.changeListeners.clear();
    this.pendingSegments = [];
    this.top = [];
  }
  // --- apply -------------------------------------------------------------------
  /** Build the {@link FlatFoldError} for a locate failure at `depth` of `change`, capturing the
   *  level the search actually ran against. Every one of these means the same thing — the stream
   *  and this tree disagree about what the tree holds — and the ONLY way to say which side is
   *  wrong is to see both, so they are captured here rather than described in a message. */
  foldError(what, change, depth, list, schema) {
    return new FlatFoldError(what, this._qid, change, depth, list, schema);
  }
  applyAt(list, schema, change, depth, enrichRemoves) {
    const path = change.path;
    if (depth === path.length) {
      return this.applyOp(list, schema, change, depth, enrichRemoves);
    }
    const seg = path[depth];
    const child = schema.relationships[seg.rel]?.child;
    if (!child) return false;
    const { found, index } = binarySearch(list, seg.parentRow, schema.sort);
    if (!found) throw this.foldError("parent not found at path hop (inconsistent stream)", change, depth, list, schema);
    const node = list[index];
    const changed = this.applyAt(node.rels[seg.rel], child, change, depth + 1, enrichRemoves);
    if (changed) node.out = null;
    return changed;
  }
  applyOp(list, schema, change, depth, enrichRemoves) {
    const op = change.op;
    if (op.tag === "add") return this.applyAdd(list, schema, op.node);
    if (op.tag === "remove") {
      const removed = this.applyRemove(list, schema, op.row, change, depth);
      if (removed && enrichRemoves) op.node = toWireNode(removed, schema);
      return removed !== null;
    }
    return this.applyEdit(list, schema, op.old, op.new, change, depth);
  }
  applyAdd(list, schema, wnode) {
    const at = binarySearch(list, wnode.row, schema.sort);
    if (at.found) {
      list[at.index].rc += 1;
      return false;
    }
    list.splice(at.index, 0, buildNode(wnode, schema));
    return true;
  }
  /** Drop one path to `row`. Returns the evicted {@link Node} (its full subtree intact) when the
   *  last path went away — `null` when another path still holds it (rc decremented, nothing left
   *  the result). */
  applyRemove(list, schema, row, change, depth) {
    const at = binarySearch(list, row, schema.sort);
    if (!at.found) throw this.foldError("remove of a non-existent node", change, depth, list, schema);
    const node = list[at.index];
    if (node.rc === 1) {
      list.splice(at.index, 1);
      return node;
    }
    node.rc -= 1;
    return null;
  }
  applyEdit(list, schema, oldRow, newRow, change, depth) {
    if (rowsEqual(oldRow, newRow)) return false;
    const sort = schema.sort;
    if (compareRows(oldRow, newRow, sort) === 0) {
      const at = binarySearch(list, oldRow, sort);
      if (!at.found) throw this.foldError("edit of a non-existent node", change, depth, list, schema);
      list[at.index].row = newRow;
      list[at.index].out = null;
      return true;
    }
    const oldAt = binarySearch(list, oldRow, sort);
    if (!oldAt.found) throw this.foldError("edit old node does not exist", change, depth, list, schema);
    const oldPos = oldAt.index;
    const oldRc = list[oldPos].rc;
    const raw = binarySearch(list, newRow, sort);
    const found = raw.found;
    const pos = raw.index;
    const oldEntry = cloneNode(list[oldPos]);
    if (oldRc === 1 && (pos === oldPos || pos - 1 === oldPos)) {
      list[oldPos].row = newRow;
      list[oldPos].out = null;
      return true;
    }
    const newRc = oldRc - 1;
    let adjusted;
    if (newRc === 0) {
      list.splice(oldPos, 1);
      adjusted = oldPos < pos ? pos - 1 : pos;
    } else {
      list[oldPos].rc = newRc;
      adjusted = pos;
    }
    if (found) {
      const existingRc = list[adjusted].rc;
      list[adjusted].row = newRow;
      list[adjusted].rc = existingRc + 1;
      list[adjusted].out = null;
    } else {
      oldEntry.row = newRow;
      oldEntry.rc = 1;
      list.splice(adjusted, 0, oldEntry);
    }
    return true;
  }
  // --- projection (memoized, structurally shared) -----------------------------
  project(node, schema, types) {
    if (node.out !== null) return node.out;
    const obj = {};
    for (let i = 0; i < schema.columns.length; i++) {
      const v = node.row[i];
      if (v === void 0) continue;
      if (types?.select !== void 0 && types.select[i] === false) continue;
      obj[schema.columns[i]] = types?.columnTypes[i] === "json" && typeof v === "string" ? JSON.parse(v) : v;
    }
    for (const rel of schema.relationships) {
      if (rel.child === null) continue;
      const childList = node.rels[rel.slot] ?? [];
      const ct = types?.rels[rel.slot];
      if (rel.project) {
        const cell = childList.length > 0 ? childList[0].row[rel.project.col] : rel.project.identity;
        const projType = ct?.columnTypes[rel.project.col];
        obj[rel.name] = projType === "json" && typeof cell === "string" ? JSON.parse(cell) : cell;
        continue;
      }
      obj[rel.name] = rel.child.singular ? childList.length > 0 ? this.project(childList[0], rel.child, ct) : null : childList.map((c) => this.project(c, rel.child, ct));
    }
    node.out = obj;
    return obj;
  }
  /** Notify data subscribers with the current {@link data}. Normally driven by a fold ({@link
   *  applyChanges}/{@link flush}); the Store also calls it directly to land a seed retirement whose
   *  accompanying fold was empty (see {@link retireSeed}). */
  notify() {
    const d = this.data;
    for (const l of this.listeners) l(d);
  }
};

// ../../packages/client/src/store.ts
var Store = class {
  /** Type-safe query entry: `store.query.issue.where.closed(false).materialize()`. */
  query;
  schema;
  backend;
  nextId = 1;
  views = /* @__PURE__ */ new Map();
  asts = /* @__PURE__ */ new Map();
  syncLeases = /* @__PURE__ */ new Map();
  // Pulse subscriptions (design 429), keyed by their Store id; `pulseSet` is the one public handle.
  pulses = /* @__PURE__ */ new Map();
  pulseSet;
  // SSR seeds (SSR-DESIGN.md §6), keyed by `viewKey`: a view materialized for one of these is
  // seeded for first paint; a seed is consumed (dropped) the moment its query's first live
  // SNAPSHOT lands — NOT its `hello` — so the seed bridges the `hello`→snapshot gap (the live
  // data arrives on the snapshot, a round-trip after the hello) and later mounts of a now-live
  // query don't re-seed stale SSR data.
  seeds = /* @__PURE__ */ new Map();
  // Public per-query change subscribers ({@link subscribeChanges}). `undefined` until the first
  // subscription, so an app that never narrates (nor attaches devtools) pays one undefined-check per
  // routed event and nothing else — no global singleton, no hot-path instrumentation. Each listener
  // also receives the post-fold view for the qid (always the plural {@link ArrayView}, even for a
  // `.one()` query — the Store retains the list-shaped view, not the SingularView wrapper).
  changeListeners;
  // How many active change subscribers asked for removed subtrees ({@link subscribeChanges} opts).
  // `> 0` ⇒ the view reconstructs each evicted subtree onto its `remove` op before fan-out. A plain
  // counter: the cost is paid only while at least one consumer wants it, and only on real evictions.
  removedSubtreeWanted = 0;
  // Public per-query {@link ResultType} subscribers ({@link subscribeResultType}). `undefined` until
  // the first subscription. Devtools and any status-driven layer ride this instead of a private tap.
  resultTypeListeners;
  // One-shot readers ({@link readOnce}) parked on a lifecycle-backed query, keyed by qid, each fired
  // by {@link foldHydration} on the first hydration fold that lands while the query is `complete` —
  // the same gate that retires an SSR seed. `undefined` until the first parked reader.
  hydrationWaiters;
  // Whether the backend drives a per-query resultType lifecycle (`onResultType`). When it does, a
  // REMOTE query is marked PENDING (`unknown`) at register (`registerMaterialized`) so its synchronous
  // pre-sync snapshot — the optimistic/wasm backend fetches local, not-yet-synced state INSIDE
  // `registerQuery` — can't be mistaken for the authoritative answer and retire the SSR seed. When it
  // doesn't, every view stays `complete` and the first snapshot IS authoritative (unchanged behavior).
  hasResultTypeLifecycle;
  // Cross-view-atomic notification (the `Backend.onCommitBoundary` contract): while the backend is
  // delivering one commit's coherent multi-query batch, fold every affected view but DEFER each
  // view's subscriber notification, collecting the changed views here; at the commit's `end` flush
  // them all. So when any view's subscriber runs, every sibling view touched by the same commit has
  // already folded — a callback that re-reads another view sees post-commit data. `> 0` ⇒ inside a
  // commit (defer); `0` ⇒ notify inline (the prior behavior, and what a backend with no commit
  // boundary always gets). A depth counter (not a bool) is robust to any future nesting.
  commitDepth = 0;
  pendingFlush = /* @__PURE__ */ new Set();
  // The raw change-stream frames ({@link subscribeChanges}) buffered during a commit, delivered
  // together at the boundary alongside the view flush — so a change listener (narrator/devtools)
  // that re-reads ANY view also sees post-commit state, the same cross-view-atomic guarantee view
  // subscribers get. Only filled while a commit is open AND a change listener exists; otherwise it
  // stays empty and untouched (the no-narrator hot path is one undefined-check, as before).
  pendingChanges = [];
  constructor(schema, backend) {
    this.schema = schema;
    this.backend = backend;
    this.hasResultTypeLifecycle = typeof backend.onResultType === "function";
    this.backend.onEvent((qid, ev) => this.onEvent(qid, ev));
    this.backend.onCommitBoundary?.((phase) => {
      if (phase === "begin") {
        this.commitDepth++;
      } else if (this.commitDepth > 0 && --this.commitDepth === 0) {
        this.flushCommit();
      }
    });
    this.backend.onResultType?.((qid, rt) => {
      this.views.get(qid)?.setResultType(rt);
      const sync = this.syncLeases.get(qid);
      if (sync && sync.resultType !== rt) {
        sync.resultType = rt;
        if (rt === "complete" && sync.seedKey !== void 0) this.seeds.delete(sync.seedKey);
        for (const listener of sync.listeners) listener();
      }
      if (this.resultTypeListeners) for (const l of this.resultTypeListeners) l(qid, rt);
    });
    this.query = queries(this.schema, (query) => this.materialize(query), { includeLocal: true });
  }
  /** Materialize any fluent query object. Named queries subscribe remotely by `(name,args)`;
   *  ad-hoc builder queries are local-only for local-first backends.
   *
   *  `opts.onChanges` binds a narrator to this view's DIFF stream ({@link ArrayView.onChanges}) — the
   *  per-view seam that replaces filtering the store-global {@link subscribeChanges} by `qid`. It is
   *  wired BEFORE the backend registers the query, so a synchronous backend's first `snapshot` (fired
   *  inside `registerQuery`, before this returns) is delivered too. */
  materialize(query, opts) {
    const remote = typeof query.name === "string" ? { name: query.name, args: query.args } : void 0;
    return this.registerMaterialized(query.ast(), remote, opts?.onChanges).view;
  }
  /** One-shot AUTHORITATIVE read: materialize `query`, wait until its result is server-authoritative
   *  ({@link ResultType} `"complete"`) AND its authoritative rows have folded, read the data once, then
   *  destroy the view — resolving with the plain result rather than a live subscription. Rejects if
   *  the query enters the `"error"` state. Use it for exports, imports, undo snapshots — anywhere
   *  that wants the current answer as a value.
   *
   *  A synchronous local-first backend (wasm/replica) has already delivered the first snapshot inside
   *  {@link materialize}, so the view is `"complete"` on entry and this settles on the next microtask
   *  without ever attaching a listener. A lifecycle backend (optimistic / normalized) registers the
   *  query PENDING (`unknown`) and hydrates it in two steps — it flips the query to `complete` FIRST
   *  and delivers the authoritative rows right after, as a `catchUp` batch (an explicit empty one
   *  when nothing folds) — so reading at the flip would return the pre-sync (empty) view. The read
   *  therefore settles on the first hydration fold (`snapshot` or `catchUp`) that lands while the
   *  query is `complete`: exactly the gate {@link retireSeedIfLive} uses for an SSR seed. The query is
   *  NEVER left subscribed — the view is destroyed before the promise settles either way. (A remote
   *  query that never hydrates leaves the promise pending, exactly as a `resultType` poll would; race
   *  a timeout at the call site if you need one.) */
  readOnce(query) {
    const view = this.materialize(query);
    return new Promise((resolve, reject) => {
      let settled = false;
      let detach;
      const finish = () => {
        settled = true;
        detach?.();
        this.hydrationWaiters?.delete(view.qid);
      };
      const fail = () => {
        finish();
        view.destroy();
        reject(new Error("store.readOnce: query entered the error result state"));
      };
      const succeed = () => {
        finish();
        const data = view.data;
        queueMicrotask(() => {
          view.destroy();
          resolve(data);
        });
      };
      if (view.resultType === "complete") {
        const data = view.data;
        view.destroy();
        resolve(data);
        return;
      }
      if (view.resultType === "error") {
        fail();
        return;
      }
      (this.hydrationWaiters ??= /* @__PURE__ */ new Map()).set(view.qid, succeed);
      detach = this.subscribeResultType((qid, rt) => {
        if (!settled && qid === view.qid && rt === "error") fail();
      });
    });
  }
  /** True when the backend can retain a remote named query independently from the local
   *  materialized AST view. React uses this to keep one local view per AST while still sending
   *  every mounted `(name,args)` lease through the backend. */
  canRetainRemoteQueries() {
    return typeof this.backend.retainRemoteQuery === "function" && typeof this.backend.releaseRemoteQuery === "function";
  }
  /** Build one local AST view, with remote syncing retained separately through the returned
   *  handle. This is a lower-level API for UI bindings; ordinary app code should keep using
   *  `materialize(query)`. */
  createCachedQueryView(query) {
    const { qid, view } = this.registerMaterialized(query.ast(), void 0);
    let destroyed = false;
    return {
      view,
      retain: (nextQuery) => this.retainRemote(nextQuery, qid),
      destroy: () => {
        if (destroyed) return;
        destroyed = true;
        view.destroy();
      }
    };
  }
  /** Retain a named remote query purely for normalized/local-first coverage. This does not
   *  register or materialize the query AST locally, so React can keep server sync coverage alive
   *  without subscribing to the broad coverage result tree. */
  retainSyncQuery(query) {
    if (!this.canRetainRemoteQueries()) {
      throw new Error(
        "store.retainSyncQuery: this backend cannot retain a remote query without a local view."
      );
    }
    const ast = query.ast();
    const remote = typeof query.name === "string" ? { name: query.name, args: query.args } : void 0;
    if (!remote) {
      throw new Error("store.retainSyncQuery: sync-only coverage requires a named query.");
    }
    const qid = this.nextId++;
    const state = { resultType: "unknown", listeners: /* @__PURE__ */ new Set(), released: false, seedKey: stableKey(ast) };
    this.syncLeases.set(qid, state);
    try {
      this.backend.retainRemoteQuery?.(qid, remote, qid, ast);
    } catch (e) {
      this.syncLeases.delete(qid);
      throw e;
    }
    return {
      get resultType() {
        return state.resultType;
      },
      subscribe: (listener) => {
        state.listeners.add(listener);
        listener();
        return () => {
          state.listeners.delete(listener);
        };
      },
      release: () => {
        if (state.released) return;
        state.released = true;
        this.syncLeases.delete(qid);
        this.backend.releaseRemoteQuery?.(qid);
      }
    };
  }
  /** The Store's PULSE subscriptions (design 429): notification without payload. `add` a named
   *  query and the server reports — through {@link PulseSet.onChanged}, once per commit burst —
   *  that its result moved, without ever sending a row; nothing is materialized locally and a
   *  pulse is free to attach (no snapshot). One handle per store. Throws on a backend whose
   *  source cannot pulse (the in-process engine, a pre-429 server). */
  pulse() {
    if (this.pulseSet) return this.pulseSet;
    const backend = this.backend;
    if (typeof backend.pulseQuery !== "function" || typeof backend.releasePulse !== "function" || typeof backend.onPulse !== "function") {
      throw new Error("store.pulse: this backend cannot pulse queries (it needs a remote source that speaks design 429).");
    }
    const changedListeners = /* @__PURE__ */ new Set();
    const readyListeners = /* @__PURE__ */ new Set();
    backend.onPulse((frame) => {
      const ready = [];
      for (const id of frame.ready ?? []) {
        const p = this.pulses.get(id);
        if (!p || p.released) continue;
        p.ready = true;
        ready.push(id);
      }
      const changed = frame.changed.filter((id) => this.pulses.get(id)?.released === false);
      if (ready.length > 0) for (const l of readyListeners) l(ready);
      if (changed.length > 0) for (const l of changedListeners) l(changed);
    });
    const pulses = this.pulses;
    this.pulseSet = {
      add: (query) => {
        const remote = typeof query.name === "string" ? { name: query.name, args: query.args } : void 0;
        if (!remote) throw new Error("store.pulse: a pulse requires a named query.");
        const id = this.nextId++;
        const state = { ready: false, released: false };
        pulses.set(id, state);
        try {
          backend.pulseQuery(id, remote);
        } catch (e) {
          pulses.delete(id);
          throw e;
        }
        return {
          id,
          get ready() {
            return state.ready;
          },
          release: () => {
            if (state.released) return;
            state.released = true;
            pulses.delete(id);
            backend.releasePulse(id);
          }
        };
      },
      onChanged: (listener) => {
        changedListeners.add(listener);
        return () => {
          changedListeners.delete(listener);
        };
      },
      onReady: (listener) => {
        readyListeners.add(listener);
        return () => {
          readyListeners.delete(listener);
        };
      },
      get size() {
        return pulses.size;
      }
    };
    return this.pulseSet;
  }
  /** Apply a batch of mutations (object rows → positional). Resolves when the backend has
   *  accepted them (local: applied; remote: sent). The resulting view updates flow back via
   *  the backend's event stream. */
  write(fn) {
    return Promise.resolve(this.backend.mutate(this.collectMutations(fn)));
  }
  /** Direct-commit write to LOCAL-only tables (`201-LOCAL-ONLY-TABLES-DESIGN.md` §6): the
   *  client-authoritative path for selection state, draft text, view prefs, scratch rows. It
   *  bypasses the optimistic pending stack entirely — a local table is untracked, so it never
   *  rebases, reverts, or waits on a server confirmation (it "moves on its own").
   *
   *  Rejects a synced/tracked table (M2): a direct write to one would be un-applied on the very
   *  next server rewind. Local writes also must NOT live inside a replayable mutator (M1) — the
   *  server runs the mutator from `args` alone and cannot see local tables; use this instead.
   *  Same keyed `WriteTx` shape as {@link write}. `async` so the no-seam / M2 guards surface as a
   *  REJECTED promise (the `Promise<void>` contract) rather than a synchronous throw that escapes a
   *  caller's `.catch` and crashes the event handler / render frame. */
  async writeLocal(fn) {
    if (!this.backend.writeLocal) {
      throw new Error(
        "store.writeLocal: this backend has no local-write seam (local-only tables need the wasm/optimistic backend)."
      );
    }
    const muts = this.collectMutations(fn);
    for (const m of muts) {
      if (!this.schema.tables[m.table]?.local) {
        throw new Error(
          `store.writeLocal: "${m.table}" is not a local-only table \u2014 use store.write or a named mutator (M2).`
        );
      }
    }
    this.backend.writeLocal(muts);
  }
  /** Drain a keyed `WriteTx` callback into positional {@link Mutation}s (shared by
   *  {@link write} / {@link writeLocal}; json columns stringified, rows in column order). */
  collectMutations(fn) {
    const muts = [];
    const tx = {
      add: (t, row) => muts.push({ op: "add", table: t, row: this.positionalize(t, row) }),
      remove: (t, row) => muts.push({ op: "remove", table: t, row: this.positionalize(t, row) }),
      edit: (t, o, n) => muts.push({ op: "edit", table: t, old: this.positionalize(t, o), new: this.positionalize(t, n) })
    };
    fn(tx);
    return muts;
  }
  // --- SSR (SSR-DESIGN.md §6) ---------------------------------------------------
  /** Seed a query's first-paint snapshot from a `POST /query` response (server side): convert the
   *  assembled rows to the view's projected shape and stash them by `viewKey`. A view materialized
   *  for this AST (during the synchronous render) reads the seed; {@link dehydrate} serializes it. */
  seedAssembled(ast, rows, cvMin) {
    this.seeds.set(stableKey(ast), { rows: this.assembleSnapshot(ast, rows), cvMin });
  }
  /** The dehydrated first-paint cache for every preloaded query — embed it in the HTML and pass it
   *  to {@link hydrate} in the browser (SSR-DESIGN.md §6.2). */
  dehydrate() {
    return Object.fromEntries(this.seeds);
  }
  /** Seed the browser store from the server's {@link dehydrate} output (SSR-DESIGN.md §6.2): each
   *  view materialized for a hydrated AST shows these rows until its first live `hello` reconciles. */
  hydrate(state) {
    for (const [key, snap] of Object.entries(state)) this.seeds.set(key, snap);
  }
  /** A query's hydrated first-paint snapshot, by `viewKey` — what React's `getServerSnapshot`
   *  reads so an SSR render (and the matching client hydration pass) sees the seeded rows without
   *  opening a subscription. */
  seedSnapshot(viewKey) {
    return this.seeds.get(viewKey);
  }
  primaryKeyFor(table) {
    const meta = this.schema.tables[table];
    if (!meta) throw new Error(`unknown table: ${table}`);
    return meta.primaryKey;
  }
  /** Convert assembled (nested-by-name) rows (SSR-DESIGN.md §3.3) into the view's projected result
   *  shape: spread `cols` (parsing json columns), recurse into each relationship by its alias
   *  (plural → array, `.one()` → object/null, `countAs` → bare scalar). */
  assembleSnapshot(ast, rows) {
    return rows.map((row) => this.assembleNode(ast, row));
  }
  assembleNode(ast, node) {
    const cols = this.columns(ast.table);
    const out = {};
    for (const [name, v] of Object.entries(node.cols ?? {})) {
      out[name] = cols[name]?.type === "json" && typeof v === "string" ? JSON.parse(v) : v;
    }
    for (const sub of ast.related ?? []) {
      const alias = sub.subquery.alias;
      if (alias === void 0 || !(alias in node)) continue;
      const child = node[alias];
      if (Array.isArray(child)) {
        out[alias] = child.map((c) => this.assembleNode(sub.subquery, c));
      } else if (child !== null && typeof child === "object") {
        out[alias] = this.assembleNode(sub.subquery, child);
      } else {
        out[alias] = child;
      }
    }
    return out;
  }
  // --- internals ---------------------------------------------------------------
  registerMaterialized(ast, remote, onChanges) {
    const qid = this.nextId++;
    this.asts.set(qid, ast);
    const view = this.views.get(qid) ?? this.views.set(qid, new FlatArrayView(void 0, void 0, qid)).get(qid);
    const baseDestroy = view.destroy.bind(view);
    view.destroy = () => {
      if (this.views.delete(qid)) {
        this.asts.delete(qid);
        this.backend.unregisterQuery(qid);
      }
      baseDestroy();
    };
    if (onChanges) view.onChanges(onChanges);
    const seed = this.seeds.get(stableKey(ast));
    if (seed) view.seed(seed.rows);
    if (remote !== void 0 && this.hasResultTypeLifecycle) view.setResultType("unknown");
    try {
      this.backend.registerQuery(qid, ast, remote);
    } catch (e) {
      this.views.delete(qid);
      this.asts.delete(qid);
      throw e;
    }
    return { qid, view: ast.one ? new SingularView(view) : view };
  }
  retainRemote(query, localQueryId) {
    const remote = typeof query.name === "string" ? { name: query.name, args: query.args } : void 0;
    if (!remote || !this.canRetainRemoteQueries()) return () => {
    };
    const qid = this.nextId++;
    this.backend.retainRemoteQuery?.(qid, remote, localQueryId, query.ast());
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.backend.releaseRemoteQuery?.(qid);
    };
  }
  onEvent(qid, ev) {
    if (ev.type === "hello") {
      const ast = this.asts.get(qid);
      const types = ast ? this.viewTypes(ev.schema, ast) : void 0;
      const view = this.views.get(qid) ?? this.views.set(qid, new FlatArrayView(void 0, void 0, qid)).get(qid);
      view.reset(ev.schema, types);
    } else if (ev.type === "snapshot") {
      this.foldHydration(qid, ev.adds, "snapshot");
    } else if (ev.catchUp) {
      this.foldHydration(qid, ev.events, "snapshot");
    } else {
      this.applyAndTrack(qid, ev.events, "batch");
    }
    if (this.changeListeners) {
      if (this.commitDepth > 0) this.pendingChanges.push([qid, ev]);
      else {
        const view = this.views.get(qid);
        for (const l of this.changeListeners) l(qid, ev, view);
      }
    }
  }
  /** Retire a view's SSR seed — from the view (so `data` switches from the seed to the maintained
   *  tree) AND from the seeds map (so no later mount re-seeds a now-live query) — but ONLY once the
   *  query is AUTHORITATIVE (`resultType === "complete"`). Called BEFORE the fold it accompanies, so
   *  that fold's notify already reflects the live tree with no empty gap. Idempotent.
   *
   *  The gate is the fix for the synchronous optimistic/wasm backend: it fires a query's FIRST snapshot
   *  from LOCAL, not-yet-synced state while the query is still `unknown` (`registerMaterialized` marks a
   *  lifecycle-backed remote view `unknown` up front for exactly this), then delivers the authoritative
   *  rows one event later as a `catchUp` batch — having already flipped the query to `complete`. So the
   *  seed survives the pre-sync snapshot (`unknown` ⇒ skip) and retires on the catch-up (`complete` ⇒
   *  retire). A lifecycle-LESS backend (pure wasm, the SSR one-shot, tests) is `complete` from creation,
   *  so its first snapshot retires the seed exactly as before this gate existed. */
  retireSeedIfLive(qid) {
    const view = this.views.get(qid);
    if (!view || view.resultType !== "complete") return false;
    const retired = view.retireSeed();
    if (retired && this.seeds.size > 0) {
      const ast = this.asts.get(qid);
      if (ast) this.seeds.delete(stableKey(ast));
    }
    return retired;
  }
  /** Retire the SSR seed (if authoritative) and fold the accompanying hydration delta — BEFORE the
   *  fold so its notify already reflects the live tree with no empty gap. The subtlety: a hydration
   *  can fold NOTHING — a 0-row authoritative result, or one whose rows are already present in `top`
   *  (a query whose result is fully covered by an already-hydrated sibling: the shared rows dedup to
   *  zero net base mutations). Then {@link FlatArrayView.applyChanges} notifies nothing, so the
   *  seed→tree switch would never reach subscribers and the view freezes on the stale seed. Guard
   *  against that: if the seed retired but the fold was a no-op, force the handoff notify (inline, or
   *  via the commit-boundary flush). Flash-safe — the forced notify only fires when there was nothing
   *  to fold, so `data` is already the correct live tree by then. */
  foldHydration(qid, events, phase) {
    const retired = this.retireSeedIfLive(qid);
    const view = this.views.get(qid);
    if (!view) return;
    const deferring = this.commitDepth > 0;
    const changed = view.applyChanges(events, this.removedSubtreeWanted > 0, deferring, phase);
    if (deferring) {
      if (changed || retired) this.pendingFlush.add(view);
    } else if (retired && !changed) {
      view.notify();
    }
    const waiter = this.hydrationWaiters?.get(qid);
    if (waiter && view.resultType === "complete") waiter();
  }
  /** Fold a batch into its view, then notify now or — inside a commit bracket — defer the view's
   *  notification to the commit boundary, so all sibling views fold first (cross-view-atomic
   *  notification; see `commitDepth`). */
  applyAndTrack(qid, events, phase) {
    const view = this.views.get(qid);
    if (!view) return;
    const deferring = this.commitDepth > 0;
    if (view.applyChanges(events, this.removedSubtreeWanted > 0, deferring, phase) && deferring) {
      this.pendingFlush.add(view);
    }
  }
  /** Deliver everything deferred during the just-ended commit, after every affected view has folded
   *  (cross-view-atomic notification): view subscribers first, then the raw change stream
   *  (narrators/devtools), each frame in arrival order. A throwing listener does not stop the others
   *  — the first error is re-raised only once the whole flush completes (mirroring the backend's
   *  per-query isolation). View subscribers run before change listeners, preserving the per-event
   *  order that held before coalescing (a view's subscribers fired before its change frame). */
  flushCommit() {
    const views = this.pendingFlush.size > 0 ? [...this.pendingFlush] : [];
    if (views.length) this.pendingFlush.clear();
    const changes = this.pendingChanges.length > 0 ? this.pendingChanges.splice(0) : [];
    if (views.length === 0 && changes.length === 0) return;
    let firstError;
    let hasError = false;
    const note = (e) => {
      if (!hasError) {
        hasError = true;
        firstError = e;
      }
    };
    for (const view of views) {
      try {
        view.flush();
      } catch (e) {
        note(e);
      }
    }
    if (this.changeListeners) {
      for (const [qid, ev] of changes) {
        const view = this.views.get(qid);
        for (const l of this.changeListeners) {
          try {
            l(qid, ev, view);
          } catch (e) {
            note(e);
          }
        }
      }
    }
    if (hasError) throw firstError;
  }
  /** Subscribe to the raw per-query {@link ChangeEvent} stream (hello / snapshot / batch) the Store
   *  routes to its views, tagged by `qid`. Fired AFTER the event is folded — and, for a commit that
   *  fans out to several queries (the in-process engine's `onCommitBoundary`), after EVERY view in
   *  that commit has folded — so a listener that re-reads ANY view (its own or a sibling) sees
   *  post-commit state, never a torn mid-commit one. Frames keep their arrival (engine-dispatch)
   *  order, one per affected query. This is the supported way to drive change-derived layers
   *  (e.g. {@link resolveChange} → @rindle/narrator, or a devtools pane) off a live store — attach
   *  BEFORE `materialize` to catch a synchronous backend's first `hello`+`snapshot`.
   *  The per-query view `WireSchema` rides the `hello` frame (also readable via `view.schema`).
   *
   *  The third listener arg is the post-fold {@link ArrayView} for this `qid` (so a template wanting
   *  list context — current `data`, `schema`, `resultType` — needn't look it up). It is ALWAYS the
   *  plural view, even for a top-level `.one()` query: the Store retains the list-shaped view, not the
   *  SingularView wrapper handed back from `materialize`. `undefined` only if the view is mid-teardown.
   *
   *  `opts.removedSubtree` enriches every `remove` op on this stream with the full removed subtree
   *  ({@link FlatOp.node}), so a consumer can resolve a removed row's nested subs exactly as on an
   *  `add` (a bare remove carries only the leaving row). It is reconstructed client-side from the
   *  view — no wire/engine cost — and paid only on real evictions while at least one subscriber asks.
   *
   *  Returns a detach function; multiple listeners may attach. */
  subscribeChanges(listener, opts) {
    (this.changeListeners ??= /* @__PURE__ */ new Set()).add(listener);
    if (opts?.removedSubtree) this.removedSubtreeWanted++;
    return () => {
      if (this.changeListeners?.delete(listener) && opts?.removedSubtree) this.removedSubtreeWanted--;
    };
  }
  /** Subscribe to per-query {@link ResultType} transitions (the server-channel lifecycle the backend
   *  pushes — `unknown` → `complete`, etc.), tagged by `qid`. Fired only on a CHANGE (never replayed
   *  on attach; read `view.resultType` for the current value). The supported seam for a status-driven
   *  layer (a devtools pane). Returns a detach function; multiple listeners may attach. */
  subscribeResultType(listener) {
    (this.resultTypeListeners ??= /* @__PURE__ */ new Set()).add(listener);
    return () => {
      this.resultTypeListeners?.delete(listener);
    };
  }
  // --- dev-only introspection (DEBUG-TOOLS-BROWSER-DESIGN §2/§6.2) --------------
  // A single read-only accessor per the design's "surface, not instrument" rule. Only ever called
  // by `@rindle/devtools` (imported in dev); inert otherwise. The delta + resultType taps devtools
  // also needs are the SUPPORTED `subscribeChanges` / `subscribeResultType` seams above.
  /** A read-only snapshot of every live materialized view (DEBUG-TOOLS-BROWSER-DESIGN §4.2): its
   *  qid, AST, {@link ResultType}, row count, and a capped row sample. Built fresh on each call from
   *  the live `views`/`asts` maps — never cached, never mutating. `sampleRows` caps the per-query
   *  peek (default 50) so a large view doesn't bloat the snapshot. */
  __inspect(sampleRows = 50) {
    const queries2 = [];
    for (const [qid, view] of this.views) {
      const ast = this.asts.get(qid);
      if (!ast) continue;
      const data = view.data;
      queries2.push({
        qid,
        ast,
        resultType: view.resultType,
        rowCount: data.length,
        sample: sampleRows >= data.length ? data : data.slice(0, sampleRows)
      });
    }
    return { queries: queries2 };
  }
  columns(table) {
    const meta = this.schema.tables[table];
    if (!meta) throw new Error(`unknown table: ${table}`);
    return meta.columns;
  }
  /** An object row → a positional cell array in the table's column order (json → string). */
  positionalize(table, obj) {
    const cols = this.columns(table);
    return Object.keys(cols).map((name) => {
      const v = obj[name];
      if (cols[name].type === "json" && v != null && typeof v === "object") return JSON.stringify(v);
      return v ?? null;
    });
  }
  /** The per-level column types parallel to the WireSchema, so the view parses json columns. */
  viewTypes(ws, ast) {
    const cols = this.columns(ast.table);
    const columnTypes = ws.columns.map((name) => cols[name]?.type ?? "string");
    const rels = {};
    for (const rel of ws.relationships) {
      if (!rel.child) continue;
      const sub = (ast.related ?? []).find((r) => r.subquery.alias === rel.name);
      if (sub) rels[rel.slot] = this.viewTypes(rel.child, sub.subquery);
    }
    if (ast.select === void 0) return { columnTypes, rels };
    const selected = new Set(ast.select);
    return { columnTypes, rels, select: ws.columns.map((name) => selected.has(name)) };
  }
};

// ../../packages/remote/src/protocol.ts
var enc2 = new TextEncoder();
var FNV_OFFSET = 0xcbf29ce484222325n;
var FNV_PRIME = 0x00000100000001b3n;
var MASK = 0xffffffffffffffffn;
var Fnv = class {
  h = FNV_OFFSET;
  byte(b) {
    this.h = (this.h ^ BigInt(b & 255)) * FNV_PRIME & MASK;
  }
  u8(v) {
    this.byte(v);
  }
  /** A `u32` little-endian (matching Rust `v.to_le_bytes()`). */
  u32(v) {
    this.byte(v & 255);
    this.byte(v >>> 8 & 255);
    this.byte(v >>> 16 & 255);
    this.byte(v >>> 24 & 255);
  }
  /** A length-prefixed string: `u32(byteLen)` then the UTF-8 bytes (disambiguates joins). */
  s(str) {
    const bytes = enc2.encode(str);
    this.u32(bytes.length);
    for (const b of bytes) this.byte(b);
  }
};
function hashLevel(f, ws) {
  f.u8(83);
  f.u32(ws.columns.length);
  for (const c of ws.columns) f.s(c);
  f.u32(ws.primaryKey.length);
  for (const pk of ws.primaryKey) f.s(ws.columns[pk]);
  f.u32(ws.sort.length);
  for (const [c, asc] of ws.sort) {
    f.s(ws.columns[c]);
    f.u8(asc ? 1 : 0);
  }
  f.u8(ws.singular ? 1 : 0);
  f.u32(ws.relationships.length);
  for (const r of ws.relationships) {
    f.s(r.name);
    if (r.child) {
      f.u8(1);
      hashLevel(f, r.child);
      if (r.project) {
        f.u8(1);
        f.s(r.child.columns[r.project.col]);
      } else {
        f.u8(0);
      }
    } else {
      f.u8(0);
    }
  }
}
function schemaFp(ws) {
  const f = new Fnv();
  hashLevel(f, ws);
  return f.h.toString(16).padStart(16, "0");
}
function cv(batch) {
  return batch.cv === void 0 ? {} : { cv: batch.cv };
}
var ProtocolError = class extends Error {
  kind;
  constructor(kind, message) {
    super(message);
    this.kind = kind;
    this.name = "ProtocolError";
  }
};
var Subscriber = class {
  epoch;
  schemaFp;
  emit;
  phase = "snapshot";
  lastSeq = 0;
  constructor(hello, emit) {
    this.emit = emit;
    if (hello.comparatorVersion !== COMPARATOR_VERSION) {
      throw new ProtocolError(
        "comparator-mismatch",
        `comparator version ${hello.comparatorVersion} != ${COMPARATOR_VERSION}`
      );
    }
    const computed = schemaFp(hello.schema);
    if (computed !== hello.schemaFp) {
      throw new ProtocolError("schema-mismatch", `advertised fp ${hello.schemaFp} != computed ${computed}`);
    }
    this.epoch = hello.epoch;
    this.schemaFp = hello.schemaFp;
    emit({ type: "hello", schema: hello.schema, comparatorVersion: hello.comparatorVersion });
  }
  /** Apply one incremental batch (or the seq-0 snapshot). Returns `"duplicate"` for an
   *  already-applied seq (discarded — rc ops are not idempotent); throws {@link ProtocolError}
   *  on a gap / epoch / schema mismatch (the caller re-hydrates). */
  apply(batch) {
    if (batch.epoch !== this.epoch) {
      throw new ProtocolError("epoch-mismatch", `expected epoch ${this.epoch}, got ${batch.epoch}`);
    }
    if (batch.schemaFp !== this.schemaFp) {
      throw new ProtocolError("schema-mismatch", `expected fp ${this.schemaFp}, got ${batch.schemaFp}`);
    }
    if (this.phase === "snapshot") {
      if (batch.seq === 0) {
        this.phase = "live";
        this.lastSeq = 0;
        this.emit({ type: "snapshot", adds: batch.events, last: true, ...cv(batch) });
        return "applied";
      }
      throw new ProtocolError("gap", `expected the seq-0 snapshot, got seq ${batch.seq}`);
    }
    const expected = this.lastSeq + 1;
    if (batch.seq < expected) return "duplicate";
    if (batch.seq > expected) {
      throw new ProtocolError("gap", `expected seq ${expected}, got ${batch.seq}`);
    }
    this.lastSeq = batch.seq;
    this.emit({ type: "batch", events: batch.events, ...cv(batch) });
    return "applied";
  }
};

// ../../packages/remote/src/framing.ts
var FLAT_FRAMING = {
  mode: "flat",
  hello: (msg) => msg.t === "hello" ? { queryId: msg.queryId, hello: msg.hello, ...msg.bootId !== void 0 ? { bootId: msg.bootId } : {} } : void 0,
  batch: (msg) => msg.t === "batch" ? { queryId: msg.queryId, batch: msg.batch } : void 0,
  open: (hello, emit) => new Subscriber(hello, emit)
};

// ../../packages/remote/src/query-error.ts
var MAX_RETRY_DELAY_MS = 3e4;
var DEFAULT_RETRY_DELAY_MS = 500;
function retryDelayMs(attempt, retryAfterMs, jitter = Math.random()) {
  const base = Math.max(retryAfterMs ?? DEFAULT_RETRY_DELAY_MS, 1);
  const backoff = Math.min(base * 2 ** attempt, MAX_RETRY_DELAY_MS);
  return Math.round(backoff * (1 + jitter * 0.25));
}

// ../../packages/remote/src/subscribe.ts
function defaultSubscribeTarget(request) {
  return { name: request.remote.name, args: request.remote.args };
}
function subscribeMessage(request, target) {
  const mode = request.mode === "normalized" || request.mode === "pulse" ? { mode: request.mode } : {};
  if (target.leaseToken !== void 0) {
    return { t: "subscribe", queryId: request.queryId, leaseToken: target.leaseToken, ...mode };
  }
  return { t: "subscribe", queryId: request.queryId, name: target.name, args: target.args, ...mode };
}
function isThenable(value) {
  return value != null && typeof value.then === "function";
}

// ../../packages/remote/src/session.ts
var LEASE_RETRY_STATUS = /* @__PURE__ */ new Set([408, 425, 429, 502, 503, 504]);
var RemoteSession = class {
  framing;
  /** The CURRENT transport (undefined in pure-lazy mode until the first lease opens one). */
  transport;
  /** The ws endpoint the current transport points at (undefined for a fixed transport). */
  currentEndpoint;
  /** Builds a transport for an endpoint; undefined ⇒ fixed transport (no migration). */
  transportFactory;
  /** The connection {@link start} brings up; cleared once it has. */
  initialConnection;
  resolveSubscribe;
  queryErrorHandler;
  /** Resolve the current affinity ticket store (see {@link RemoteSessionOptions.affinity}). */
  affinityStore;
  /** The per-query event sink the concrete source's `onNormalized`/`onFlat` setter writes. */
  handler = () => {
  };
  progressHandler = () => {
  };
  pulseHandler = () => {
  };
  restartHandler = () => {
  };
  /** Opaque per-protocol hello expectation, handed to {@link Framing.open} (the normalized
   *  client-schema check; flat has none). */
  expected;
  subs = /* @__PURE__ */ new Map();
  /** One jitter draw per retry attempt for the WHOLE session (see {@link onQueryError}). */
  retryJitter = [];
  /** Queries whose subscribe is waiting for a transport to exist — endpoint-less subscribes issued
   *  in pure-lazy mode before any lease opens a transport (the lmid system query is registered by
   *  the backend at construction). Flushed when a transport comes up. */
  deferred = /* @__PURE__ */ new Set();
  /** Writes issued while no transport exists — pure-lazy mode before the first lease opens one.
   *  Each is sent, and its promise resolved, when a transport comes up; {@link close} rejects the
   *  rest. Without the hold a write in that window resolved as sent and went nowhere. */
  heldWrites = [];
  /** One warning per session when an APP lease resolves without a `wsEndpoint` in pure-lazy mode —
   *  nothing will ever open the transport, which is otherwise silent (views just stay empty). */
  warnedEndpointlessLease = false;
  /** The server's boot id (from each hello); a change means it restarted. */
  lastBootId;
  /** Once true (set by {@link close}), in-flight lease resolutions are inert — they must not open a
   *  new transport or send after teardown. */
  closed = false;
  /** True once any lease has carried a routed `wsEndpoint`. Gates onDown re-leasing so a single
   *  UNROUTED daemon keeps its pre-router behavior (recover via reconnect→resync only), not an extra
   *  lease POST during an outage. */
  sawRoutedEndpoint = false;
  /** Bumped at the start of every re-subscribe-all pass. A migrate triggered mid-pass starts a new
   *  pass (higher generation); the outer pass then aborts instead of re-subscribing queries twice. */
  resubscribeGen = 0;
  constructor(framing, connection, opts = {}) {
    this.framing = framing;
    this.resolveSubscribe = opts.resolveSubscribe ?? defaultSubscribeTarget;
    this.queryErrorHandler = opts.onQueryError ?? (() => {
    });
    const affinity = opts.affinity;
    this.affinityStore = typeof affinity === "function" ? affinity : () => affinity;
    this.transportFactory = isTransport(connection) || "transport" in connection ? void 0 : connection.factory;
    this.initialConnection = connection;
  }
  /** Bring the configured connection up. Called by the concrete source as the LAST statement of
   *  its constructor, so {@link onTransportUp} sees fully-initialized subclass fields. */
  start() {
    const connection = this.initialConnection;
    this.initialConnection = void 0;
    if (connection === void 0) return;
    if (isTransport(connection)) this.bringUp(connection, void 0);
    else if ("transport" in connection) this.bringUp(connection.transport, void 0);
    else if (connection.endpoint !== void 0) this.openEndpoint(connection.endpoint);
  }
  // --- hooks (the optimistic extras; no-ops here) --------------------------------
  /** End of {@link bringUp}, before deferred subscribes flush. The optimistic source sends `init`. */
  onTransportUp(_transport) {
  }
  /** Top of {@link resync}, before the re-subscribe pass. The optimistic source re-sends `init`,
   *  arms the push hold, and fires its `onResync` handler — synchronously, which is the ordering
   *  `OptimisticBackend` relies on to re-send pending envelopes before any post-reconnect frame
   *  releases. */
  beforeResubscribe() {
  }
  /** A subscription just failed TERMINALLY: no retry is scheduled, and a lease the route refuses
   *  outright refuses it again on every resync. Subclasses whose consumer holds release state
   *  keyed by query (`RemoteFlatSource` → the `cv` gate's snapshot-owed hold) override this to
   *  say so. Fired before the app's `onQueryError`, which is allowed to throw. */
  onSubscriptionLost(_qid) {
  }
  /** First thing in the server-message dispatch; `true` means the frame was consumed. The
   *  optimistic source takes `mutationOutcome` here, out-of-band, before any per-query handling. */
  onMessage(_msg) {
    return false;
  }
  /** After a validator opens successfully. The optimistic source flushes its held pushes on the
   *  first AUTHENTICATED hello (`state.authed`). */
  onHello(_qid, _state) {
  }
  /** A PULSE's `ready` — its structural twin of {@link onHello} (429 §5.3: the drain emits
   *  exactly one per attach, the moment the query is hydrated). A pulse never opens a validator,
   *  so this is the ONLY place a pulse's subscribe is acknowledged. The optimistic source flushes
   *  its held pushes here too, on the same `state.authed` test. */
  onPulseReady(_qid, _state) {
  }
  /** Is this query wire plumbing rather than an app query? Reserved queries bypass the app's
   *  subscribe resolver (the server resolves them from the connection's own identity) and their
   *  failures never reach the app's `onQueryError` — they are the connection's, surfaced through
   *  its own recovery. The optimistic source reserves the two system queries; nothing else does. */
  isReserved(_remote) {
    return false;
  }
  /** Resolve one subscribe's upstream target. Reserved queries take the embedded-server
   *  `{name,args}` path (the API server has no such named query, and a lease for one would be
   *  meaningless); everything else goes through the app's resolver. */
  resolveTarget(request) {
    return this.isReserved(request.remote) ? defaultSubscribeTarget(request) : this.resolveSubscribe(request);
  }
  // --- connection ----------------------------------------------------------------
  /** Wire a transport's handlers. */
  attach(transport) {
    transport.onMessage((msg) => this.onServerMsg(msg));
    transport.onReconnect?.(() => {
      this.affinityStore()?.connectionPending();
      this.resync();
    });
    transport.onDown?.(() => this.onDown());
  }
  /** Make `transport` the current one, announce identity, and (re)subscribe anything deferred. */
  bringUp(transport, endpoint) {
    this.transport = transport;
    this.currentEndpoint = endpoint;
    this.affinityStore()?.connectionPending();
    this.attach(transport);
    this.onTransportUp(transport);
    this.flushHeldWrites(transport);
    this.flushDeferred();
  }
  /** Send the writes held until a transport existed, in the order they were issued. A frame the
   *  transport refuses (an unserializable arg) rejects only its own write. */
  flushHeldWrites(transport) {
    for (const write of this.heldWrites.splice(0)) {
      try {
        transport.send(write.msg);
        write.sent();
      } catch (err) {
        write.lost(err instanceof Error ? err : new Error(String(err)));
      }
    }
  }
  /** Build + bring up a fresh transport to `endpoint` (replaceable mode only). */
  openEndpoint(endpoint) {
    if (!this.transportFactory) return;
    this.bringUp(this.transportFactory(endpoint), endpoint);
  }
  /** Migrate the whole session to a new follower: build the new transport, tear the old one
   *  down, and re-subscribe EVERY active query there (re-leasing — the old tokens are
   *  follower-local and invalid on the new node). */
  migrate(endpoint) {
    const old = this.transport;
    const carried = old?.takeUnsentWrites?.() ?? [];
    this.openEndpoint(endpoint);
    old?.close();
    for (const msg of carried) this.transport?.send(msg);
    this.resubscribeAll();
  }
  /** Re-subscribe every live query on the current transport (each re-resolves its lease). A
   *  re-subscribe can synchronously trigger a `migrate` (lease names a new endpoint), whose own
   *  re-subscribe pass supersedes this one — the generation check then aborts this pass so a query
   *  is never re-subscribed (and re-leased) twice. */
  resubscribeAll() {
    const gen = ++this.resubscribeGen;
    for (const [qid, s] of this.subs) {
      if (this.resubscribeGen !== gen) return;
      s.validator = null;
      s.resubscribing = true;
      this.subscribe(qid, s.remote);
    }
  }
  /** Flush subscribes deferred until a transport existed (e.g. the lmid query in pure-lazy mode). */
  flushDeferred() {
    if (this.deferred.size === 0) return;
    const qids = [...this.deferred];
    this.deferred.clear();
    for (const qid of qids) {
      const s = this.subs.get(qid);
      if (s) this.subscribe(qid, s.remote);
    }
  }
  /** The current follower's ws is sustainedly down — re-lease every query. Each lease carries the
   *  api-server's configured `wsEndpoint` (the read router that could return a new one is retired,
   *  design 438 §1.5): a changed one migrates the session; an unchanged one re-subscribes over the
   *  reconnecting transport. No-op for an UNROUTED daemon (no lease ever carried a `wsEndpoint`) —
   *  there is nowhere to move, so we keep the pre-router behavior and let the transport's own
   *  reconnect→resync recover. */
  onDown() {
    if (this.closed) return;
    const affinityStore = this.affinityStore();
    if (affinityStore?.enabled()) {
      affinityStore.clear();
      return;
    }
    if (!this.sawRoutedEndpoint) return;
    this.resubscribeAll();
  }
  /** On reconnect: let the subclass re-establish the session (re-`init`, the §7.5 rule-3 re-send)
   *  and re-subscribe every live query — each re-resolves its lease, so a restarted daemon
   *  re-materializes and re-leases transparently. */
  resync() {
    if (this.closed) return;
    this.beforeResubscribe();
    this.resubscribeAll();
  }
  /** Tear down the current transport and make any in-flight lease resolution inert (a late lease
   *  must NOT open a new transport after the consumer closed the client). */
  close() {
    this.closed = true;
    this.transport?.close();
    for (const s of this.subs.values()) {
      if (s.retryTimer !== void 0) clearTimeout(s.retryTimer);
    }
    this.subs.clear();
    this.deferred.clear();
    for (const write of this.heldWrites.splice(0)) {
      write.lost(new Error("[rindle-remote] write not sent: the session closed before a transport opened"));
    }
  }
  /** Send a write frame. The promise resolves once the frame is handed to a transport; with no
   *  transport yet (pure-lazy mode) the write is held until one comes up, and rejects if the
   *  session closes first. A write after {@link close} rejects: nothing would ever carry it. */
  sendWrite(msg) {
    if (this.closed) return Promise.reject(new Error("[rindle-remote] write not sent: the session is closed"));
    if (!this.transport) {
      return new Promise((sent, lost) => this.heldWrites.push({ msg, sent, lost }));
    }
    this.transport.send(msg);
    return Promise.resolve();
  }
  // --- the query surface -----------------------------------------------------------
  registerQuery(qid, remote) {
    this.subs.set(qid, {
      remote,
      validator: null,
      epoch: 0,
      resubscribing: false,
      subscribeTicket: 0,
      retryTimer: void 0,
      retryAttempt: 0,
      authed: false
    });
    this.subscribe(qid, remote);
  }
  unregisterQuery(qid) {
    const s = this.subs.get(qid);
    if (s?.retryTimer !== void 0) clearTimeout(s.retryTimer);
    this.subs.delete(qid);
    this.deferred.delete(qid);
    this.transport?.send({ t: "unsubscribe", queryId: qid });
  }
  onProgress(handler) {
    this.progressHandler = handler;
  }
  /** Subscribe `qid` as a PULSE (design 429): the same lease resolution and the same `subscribe`
   *  frame as {@link registerQuery}, with `mode:"pulse"` — the server then reports the query in
   *  connection-level {@link PulseFrame}s ({@link onPulse}) and never sends it a row. Reconnect,
   *  migration and retryable errors re-subscribe it exactly like a query. */
  pulseQuery(qid, remote) {
    this.subs.set(qid, {
      remote,
      validator: null,
      epoch: 0,
      resubscribing: false,
      subscribeTicket: 0,
      retryTimer: void 0,
      retryAttempt: 0,
      authed: false,
      pulse: true
    });
    this.subscribe(qid, remote);
  }
  unpulseQuery(qid) {
    this.unregisterQuery(qid);
  }
  /** The connection-level pulse stream (429). Dispatched on arrival, outside the per-query
   *  machinery — a pulse carries no `cv` and must never wait behind a backend's cv buffer. */
  onPulse(handler) {
    this.pulseHandler = handler;
  }
  /** Register a handler fired when the SERVER restarts (a new boot id) — an optimistic backend
   *  resets its `cv` watermark so the new daemon's reset `cv` sequence is accepted instead of
   *  dropped. A server that sends no boot id never fires it. */
  onRestart(handler) {
    this.restartHandler = handler;
  }
  // --- internals ---------------------------------------------------------------
  subscribe(qid, remote) {
    const s = this.subs.get(qid);
    if (!s) return;
    if (s.retryTimer !== void 0) {
      clearTimeout(s.retryTimer);
      s.retryTimer = void 0;
    }
    const request = { queryId: qid, remote, mode: s.pulse ? "pulse" : this.framing.mode };
    const ticket = ++s.subscribeTicket;
    const send = (target) => {
      if (this.closed) return;
      const cur = this.subs.get(qid);
      if (cur !== s || cur.subscribeTicket !== ticket) return;
      const endpoint = "leaseToken" in target ? target.wsEndpoint : void 0;
      s.authed = "leaseToken" in target;
      if (endpoint !== void 0) this.sawRoutedEndpoint = true;
      if (this.transport) {
        if (endpoint !== void 0 && endpoint !== this.currentEndpoint && this.transportFactory) {
          this.migrate(endpoint);
          return;
        }
        this.transport.send(subscribeMessage(request, target));
        return;
      }
      if (endpoint !== void 0 && this.transportFactory) {
        this.openEndpoint(endpoint);
        this.transport.send(subscribeMessage(request, target));
        return;
      }
      if ("leaseToken" in target && !this.warnedEndpointlessLease) {
        this.warnedEndpointlessLease = true;
        console.warn(
          "[rindle-remote] a query lease returned no wsEndpoint and no daemon.wsUrl/transport is configured \u2014 the live subscription cannot open. Configure the API server's rindle.wsUrl (or pass daemon.wsUrl to createRindleClient)."
        );
      }
      this.deferred.add(qid);
    };
    const fail = (err) => {
      const cur = this.subs.get(qid);
      if (cur !== s || cur.subscribeTicket !== ticket) return;
      s.resubscribing = false;
      const message = String(err?.message ?? err);
      const status = err?.status;
      if (typeof status === "number" && LEASE_RETRY_STATUS.has(status)) {
        this.onQueryError(qid, { message, code: "lease", retryable: true, status });
        return;
      }
      console.error(
        `[rindle-remote] ${this.framing.mode} query ${qid} subscribe resolution failed: ${message}`
      );
      this.reportQueryError(qid, remote, {
        message,
        code: "lease",
        retryable: false,
        ...typeof status === "number" ? { status } : {}
      });
    };
    try {
      const target = this.resolveTarget(request);
      if (isThenable(target)) void target.then(send, fail);
      else send(target);
    } catch (err) {
      fail(err);
    }
  }
  /** Hand a failed subscribe to the app layer ({@link RemoteSessionOptions.onQueryError}).
   *  {@link isReserved} queries never reach it — they are wire plumbing, not app queries, and
   *  their failures are the connection's, surfaced through its own recovery. A throwing handler
   *  must not break the session's own bookkeeping, so it is contained here. */
  reportQueryError(qid, remote, error) {
    if (this.isReserved(remote)) return;
    if (error.retryable === false) this.onSubscriptionLost(qid);
    try {
      this.queryErrorHandler(remote, error);
    } catch (handlerErr) {
      console.error("[rindle-remote] onQueryError handler threw:", handlerErr);
    }
  }
  onServerMsg(msg) {
    if (this.onMessage(msg)) return;
    if (msg.t === "affinity") {
      this.affinityStore()?.set(msg.ticket);
      return;
    }
    if (msg.t === "progress") {
      this.progressHandler(msg.frame);
      return;
    }
    if (msg.t === "pulse") {
      const changed = msg.changed.filter((id) => this.subs.get(id)?.pulse === true);
      const ready = [];
      for (const id of msg.ready ?? []) {
        const s2 = this.subs.get(id);
        if (s2?.pulse !== true) continue;
        ready.push(id);
        s2.resubscribing = false;
        s2.retryAttempt = 0;
        this.onPulseReady(id, s2);
      }
      if (changed.length > 0 || ready.length > 0) this.pulseHandler({ changed, ready });
      return;
    }
    if (msg.t === "queryError") {
      this.onQueryError(msg.queryId, msg);
      return;
    }
    const hello = this.framing.hello(msg);
    if (hello) {
      this.observeBootId(hello.bootId);
      const s2 = this.subs.get(hello.queryId);
      if (!s2) return;
      this.openValidator(hello.queryId, s2, hello.hello);
      return;
    }
    const batch = this.framing.batch(msg);
    if (!batch) return;
    const s = this.subs.get(batch.queryId);
    if (!s) return;
    this.applyBatch(batch.queryId, s, batch.batch);
  }
  /** Track the server's boot id; a change (after the first) means it restarted — fire onRestart. */
  observeBootId(bootId) {
    if (!bootId) return;
    if (this.lastBootId !== void 0 && bootId !== this.lastBootId) this.restartHandler();
    this.lastBootId = bootId;
  }
  /** Route a `queryError` by its 101 §5 classification: retryable ⇒ keep the QState (and the
   *  rows already folded downstream — 101 §6) and re-subscribe after a jittered backoff
   *  honoring `retryAfterMs`; terminal (or pre-classification servers) ⇒ drop the
   *  subscription, exactly as before. Fixes the stranded-client gap: a worker fault's or a
   *  shedding follower's error now heals end-to-end (FOLLOWER-LAG-SHED §6.3). */
  onQueryError(qid, err) {
    const s = this.subs.get(qid);
    if (!s) return;
    const mode = this.framing.mode;
    const detail = {
      ...err.code !== void 0 ? { code: err.code } : {},
      ...err.status !== void 0 ? { status: err.status } : {}
    };
    if (err.retryable !== true) {
      if (s.retryTimer !== void 0) clearTimeout(s.retryTimer);
      this.subs.delete(qid);
      console.error(`[rindle-remote] ${mode} query ${qid} subscription rejected: ${err.message}`);
      this.reportQueryError(qid, s.remote, { message: err.message, ...detail, retryable: false });
      return;
    }
    if (s.retryTimer !== void 0) return;
    s.validator = null;
    s.resubscribing = true;
    const attempt = s.retryAttempt++;
    const delay = retryDelayMs(attempt, err.retryAfterMs, this.retryJitter[attempt] ??= Math.random());
    console.warn(
      `[rindle-remote] ${mode} query ${qid} ${err.code ?? "error"} (retryable): re-subscribing in ${delay}ms: ${err.message}`
    );
    this.reportQueryError(qid, s.remote, { message: err.message, ...detail, retryable: true });
    const timer = setTimeout(() => {
      const cur = this.subs.get(qid);
      if (cur !== s) return;
      s.retryTimer = void 0;
      this.subscribe(qid, s.remote);
    }, delay);
    timer.unref?.();
    s.retryTimer = timer;
  }
  openValidator(qid, s, hello) {
    try {
      s.validator = this.framing.open(hello, (ev) => this.handler(qid, ev), this.expected);
      s.epoch = s.validator.epoch;
      s.resubscribing = false;
      s.retryAttempt = 0;
      this.onHello(qid, s);
    } catch (e) {
      s.validator = null;
      console.error(
        `[rindle-remote] ${this.framing.mode} query ${qid} subscription rejected: ${e.message}`
      );
    }
  }
  applyBatch(qid, s, batch) {
    if (!s.validator) return;
    if (batch.epoch < s.epoch) return;
    try {
      s.validator.apply(batch);
    } catch (e) {
      if (!(e instanceof ProtocolError)) throw e;
      if (s.resubscribing) return;
      s.resubscribing = true;
      s.validator = null;
      this.subscribe(qid, s.remote);
    }
  }
};
function isTransport(value) {
  return typeof value.send === "function" && typeof value.onMessage === "function";
}

// ../../packages/remote/src/transport.ts
var WsTransport = class {
  url;
  /** Reads the subprotocols to offer at each (re)connect — in affinity mode, `["rindle.v1", "aff.…"]`
   *  with the CURRENT ticket (design 438 §1.3). Undefined ⇒ offer none (today's
   *  single-daemon behavior, byte-identical). Evaluated per connect so a reconnect presents the
   *  freshest (or freshly cleared) ticket. */
  subprotocols;
  ws;
  connectTicket = 0;
  opts;
  handler = () => {
  };
  reconnectHandler = () => {
  };
  downHandler = () => {
  };
  /** Buffered as PRE-SERIALIZED frames: serialization happens at `send` time so an
   *  unserializable message (a `bigint` query arg — `JSON.stringify` throws on bigint)
   *  throws typed INTO ITS CALLER instead of detonating later inside the socket's
   *  `open` listener, where it would strand every frame queued behind it. The `msg` is kept beside
   *  the text to classify a buffered frame twice over: a raw `mutate` is the one frame a RECONNECT
   *  keeps (see the `open` listener), and `mutate` + `pushMutation` are the two a MIGRATION carries
   *  to the transport that replaces this one ({@link takeUnsentWrites}). */
  pending = [];
  open = false;
  everOpened = false;
  closedByUser = false;
  attempt = 0;
  reconnectTimer;
  /** Failed reconnect attempts after which the connection is declared "down" (fires `onDown`). */
  downThreshold;
  /** True once `onDown` has fired for the CURRENT down episode; reset on the next successful open
   *  so a later outage fires again (but a single episode fires `onDown` exactly once — no re-lease
   *  storm while a follower is gone). */
  downFired = false;
  constructor(url, opts = {}) {
    this.opts = opts;
    this.url = url;
    this.downThreshold = opts.downThreshold ?? 4;
    this.subprotocols = opts.subprotocols;
    this.ws = this.connect();
  }
  connect() {
    this.opts.onState?.("connecting");
    if (typeof this.url === "string") return this.openSocket(this.url);
    const ticket = ++this.connectTicket;
    Promise.resolve().then(this.url).then((url) => {
      if (!this.closedByUser && ticket === this.connectTicket) this.ws = this.openSocket(url);
    }).catch((error) => {
      if (this.closedByUser || ticket !== this.connectTicket) return;
      this.opts.onConnectError?.(error);
      if (!this.closedByUser) this.scheduleReconnect();
    });
    return void 0;
  }
  openSocket(url) {
    const protocols = this.subprotocols?.();
    const ws = protocols && protocols.length > 0 ? new WebSocket(url, protocols) : new WebSocket(url);
    ws.addEventListener("open", () => {
      this.open = true;
      this.opts.onState?.("open");
      this.attempt = 0;
      this.downFired = false;
      if (!this.everOpened) {
        this.everOpened = true;
        for (const m of this.pending) ws.send(m.text);
        this.pending.length = 0;
      } else {
        const writes = this.pending.filter((m) => m.msg.t === "mutate");
        this.pending.length = 0;
        this.reconnectHandler();
        for (const m of writes) ws.send(m.text);
      }
    });
    ws.addEventListener("message", (ev) => {
      this.handler(JSON.parse(typeof ev.data === "string" ? ev.data : String(ev.data)));
    });
    ws.addEventListener("close", () => {
      this.open = false;
      this.opts.onState?.("closed");
      if (!this.closedByUser) this.scheduleReconnect();
    });
    ws.addEventListener("error", () => {
      if (this.open || this.closedByUser) return;
      this.scheduleReconnect();
    });
    return ws;
  }
  scheduleReconnect() {
    if (this.reconnectTimer !== void 0) return;
    const delay = Math.min(250 * 2 ** this.attempt, 5e3);
    this.attempt++;
    if (this.everOpened && !this.downFired && this.attempt >= this.downThreshold) {
      this.downFired = true;
      this.downHandler();
    }
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = void 0;
      if (!this.closedByUser) this.ws = this.connect();
    }, delay);
  }
  send(msg) {
    let text;
    try {
      text = JSON.stringify(msg);
    } catch (e) {
      throw new Error(
        `query args must be JSON-serializable: bigint values are not supported on the live-query wire until the browser bigint lane ships (design 226) \u2014 ${e instanceof Error ? e.message : String(e)}`
      );
    }
    if (this.open) this.ws.send(text);
    else this.pending.push({ text, msg });
  }
  /** The buffered frames nothing else would rebuild, removed from the buffer. A migrating session
   *  closes this transport for good, and its `onReconnect` rebuild never runs here — so both a raw
   *  `mutate` and a `pushMutation` (which a RECONNECT drops because the optimistic backend re-sends
   *  every pending envelope from its own `onResync`, a migration never firing one) would otherwise
   *  vanish after resolving as sent. Session frames are left behind: the session rebuilds those. */
  takeUnsentWrites() {
    const carried = [];
    for (let i = this.pending.length - 1; i >= 0; i--) {
      const t = this.pending[i].msg.t;
      if (t !== "mutate" && t !== "pushMutation") continue;
      carried.unshift(this.pending[i].msg);
      this.pending.splice(i, 1);
    }
    return carried;
  }
  onMessage(handler) {
    this.handler = handler;
  }
  onReconnect(handler) {
    this.reconnectHandler = handler;
  }
  onDown(handler) {
    this.downHandler = handler;
  }
  close() {
    this.closedByUser = true;
    if (this.reconnectTimer !== void 0) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = void 0;
    }
    this.ws?.close();
  }
};

// ../../packages/remote/src/flat-source.ts
var RemoteFlatSource = class extends RemoteSession {
  sendMutation;
  resyncHandler = () => {
  };
  droppedHandler = () => {
  };
  constructor(connection, opts = {}) {
    super(FLAT_FRAMING, connection, opts);
    this.sendMutation = opts.sendMutation;
    this.start();
  }
  /** Fired SYNCHRONOUSLY when the transport re-establishes its session, before the re-subscribe
   *  pass and so before any post-reconnect frame is processed. `FlatBackend` arms its
   *  snapshot-owed hold here (426 §9.2 item 6): every query is about to re-subscribe, and a
   *  `cvMin` that released between one query's fresh snapshot and another's would show the two
   *  at different points in time. */
  onResync(handler) {
    this.resyncHandler = handler;
  }
  beforeResubscribe() {
    this.resyncHandler();
  }
  /** Fired when a query's subscription fails terminally — a refused lease, a non-retryable
   *  `queryError`. `FlatBackend` drops that query from its snapshot-owed hold here: the session
   *  has deleted the `QState` (or will re-lease into the same refusal on every resync), so the
   *  fresh snapshot the hold is waiting for is never coming, and one refused query would
   *  otherwise freeze every OTHER view on this connection for good. */
  onQueryDropped(handler) {
    this.droppedHandler = handler;
  }
  onSubscriptionLost(qid) {
    this.droppedHandler(qid);
  }
  /** Re-subscribe every registered query under a fresh epoch. The same path a gap takes, asked
   *  for from above: `FlatBackend` calls it when its `cv` buffer overflows and ordering can no
   *  longer be recovered from what it holds. */
  resubscribeAll() {
    super.resubscribeAll();
  }
  /** Eventually-consistent: the mutation is sent; the resulting batches arrive async on the
   *  stream. The promise resolves once sent (the §2.3 write asymmetry, named not leaked) — in
   *  pure-lazy mode, once the first lease opens the transport. */
  mutate(mutations) {
    if (this.sendMutation) return Promise.resolve(this.sendMutation(mutations));
    return this.sendWrite({ t: "mutate", mutations });
  }
  onFlat(handler) {
    this.handler = handler;
  }
};

// ../../packages/remote/src/backend.ts
var MAX_BUFFERED_FRAMES = 1e4;
var FlatBackend = class {
  source;
  reveal;
  handler = () => {
  };
  commitBoundary;
  resultType;
  devObservers = /* @__PURE__ */ new Set();
  /** Frames waiting for a `cvMin` that covers them, in arrival order. */
  buffer = [];
  arrivals = 0;
  /** The highest `cvMin` already applied — frames at or below it are re-deliveries. */
  appliedCv = 0;
  /** The highest `cvMin` this connection has SENT, applied or not. Kept separately from
   *  {@link appliedCv} because a watermark that arrives while the snapshot-owed hold is up is not
   *  applied then — and what clears that hold (the last owed snapshot landing, or the query going
   *  away) is not itself a frame. Without remembering it, the release waits for the NEXT progress
   *  frame, which on an idle connection never comes: the same shape as the release the 418 §2.2
   *  hold stranded in the optimistic backend (428 §10.1). */
  pendingCvMin = 0;
  /** A held `hello` per query: delivered immediately before that query's next snapshot, so the
   *  view never sits reset-but-unfilled (the no-flash re-hydrate). */
  heldHello = /* @__PURE__ */ new Map();
  /** Queries mid-re-hydrate — the snapshot-owed hold. `null` until that query's fresh snapshot
   *  arrives, then the `cv` it is stamped at. While ANY entry is unsatisfied — not arrived, or
   *  arrived ABOVE the connection's watermark — no release happens. */
  snapshotOwed = /* @__PURE__ */ new Map();
  /** Queries whose snapshot has been RELEASED at least once — the only ones that arm the hold.
   *  A query that has never shown a snapshot reads `unknown`, so there is no "before" for a
   *  release to tear it away from, and holding every other query for its first hydrate would pay
   *  the tear's price where there is no tear (a newly materialized query would stall the rest of
   *  the client for the length of its attach). */
  hydrated = /* @__PURE__ */ new Set();
  /** Queries the source has TERMINALLY given up on (a refused lease, a non-retryable
   *  `queryError`), until a fresh `hello` proves the subscription is back. Excluded from the
   *  hold: nothing re-subscribes them on their own, so arming one pins every OTHER query on the
   *  connection for the rest of its life — and, since `reset()` arms rather than clears, a daemon
   *  restart would be the thing that re-pins it rather than the way out. */
  lost = /* @__PURE__ */ new Set();
  /** Every query currently registered — the set a resync re-subscribes, minus {@link lost}. */
  registered = /* @__PURE__ */ new Set();
  constructor(source, opts = {}) {
    this.source = source;
    this.reveal = opts.reveal ?? "committed";
    this.source.onFlat((qid, ev) => this.onFrame(qid, ev));
    this.source.onProgress((frame) => this.onProgress(frame));
    this.source.onRestart?.(() => this.reset());
    this.source.onResync?.(() => this.armHold());
    this.source.onQueryDropped?.((qid) => this.dropQuery(qid));
  }
  registerQuery(qid, _ast, remote) {
    if (!remote) {
      console.error(`[rindle-remote] flat query ${qid} has no named remote identity`);
      return;
    }
    this.registered.add(qid);
    this.lost.delete(qid);
    this.source.registerQuery(qid, remote);
  }
  unregisterQuery(qid) {
    this.registered.delete(qid);
    this.hydrated.delete(qid);
    this.lost.delete(qid);
    this.forget(qid);
    this.source.unregisterQuery(qid);
  }
  /** Eventually-consistent: the mutation is sent; the resulting batches arrive async on the
   *  stream. The promise resolves once sent (the §2.3 write asymmetry, named not leaked). */
  mutate(mutations) {
    return this.source.mutate(mutations);
  }
  onEvent(handler) {
    this.handler = handler;
  }
  /** The Store defers every view's listener notification between `begin` and `end`, so one
   *  release — however many queries it touches — is observed as one change. */
  onCommitBoundary(handler) {
    this.commitBoundary = handler;
  }
  /** A flat query is `unknown` until its first snapshot is RELEASED, then `complete`.
   *
   *  Declaring the lifecycle is what makes `store.readOnce` correct on this backend. Without it
   *  the Store leaves every view `complete` from registration — the contract for an in-process
   *  engine, whose snapshot folds synchronously inside `materialize` — and `readOnce` would
   *  resolve with an empty array before the daemon had answered. There is no round trip in the
   *  local case and nothing to wait for; here there are two (lease, then subscribe) plus a
   *  worker read and a release.
   *
   *  It is also what `LiveQuery.resultType` means to a caller: "the daemon has not answered yet"
   *  is a state a service needs to distinguish from "the answer is no rows". */
  onResultType(handler) {
    this.resultType = handler;
  }
  /** A read-only snapshot of the `cv` gate, mirroring `OptimisticBackend.__inspect()`'s
   *  convention (the `__`-prefix marks it a test hook). It is what lets a harness state the flat
   *  stack's PER-STEP oracle — every released view equals the fresh query at {@link appliedCv} —
   *  rather than grading flat at quiesce alone, which is all design 428 ring 1 could do before
   *  the gate existed. Every field is state the gate already holds; nothing is computed for it. */
  __inspect() {
    return {
      appliedCv: this.appliedCv,
      bufferedFrames: this.buffer.length,
      snapshotOwed: this.snapshotOwed.size,
      heldHellos: this.heldHello.size,
      registered: this.registered.size
    };
  }
  __attachDevtoolsServerDeltas(observer) {
    this.devObservers.add(observer);
    return () => {
      this.devObservers.delete(observer);
    };
  }
  // --- the gate ---------------------------------------------------------------------
  onFrame(qid, ev) {
    if (ev.type === "hello") {
      this.buffer = this.buffer.filter((p) => p.qid !== qid);
      this.heldHello.set(qid, ev);
      this.lost.delete(qid);
      if (this.reveal === "committed" && this.hydrated.has(qid)) this.owe(qid);
      return;
    }
    const owed = ev.type === "snapshot" && this.snapshotOwed.has(qid);
    if (this.reveal === "immediate" || ev.cv === void 0) {
      this.bracket(() => this.deliver(qid, ev));
      if (owed) this.tryRelease();
      return;
    }
    this.buffer.push({ qid, ev, cv: ev.cv, arrival: this.arrivals++ });
    if (owed) {
      this.snapshotOwed.set(qid, ev.cv);
      this.tryRelease();
    }
    if (this.buffer.length > MAX_BUFFERED_FRAMES) {
      console.error(
        `[rindle-remote] flat gate buffered ${this.buffer.length} frames without a releasing progress frame; re-hydrating`
      );
      this.rehydrateAll();
    }
  }
  onProgress(frame) {
    if (this.reveal === "immediate") return;
    this.pendingCvMin = Math.max(this.pendingCvMin, frame.cvMin);
    this.tryRelease();
  }
  /** Release everything the connection's watermark covers — unless the hold is up. Called by a
   *  `progress` frame, and by the three things that CLEAR the hold without being one: an owed
   *  snapshot landing, an owing query being unregistered, and the source giving one up. */
  tryRelease() {
    const cvMin = this.pendingCvMin;
    for (const cv2 of this.snapshotOwed.values()) if (cv2 === null || cv2 > cvMin) return;
    const releasable = this.buffer.filter((p) => p.cv <= cvMin);
    this.appliedCv = Math.max(this.appliedCv, cvMin);
    if (releasable.length === 0) return;
    this.buffer = this.buffer.filter((p) => p.cv > cvMin);
    releasable.sort((a, b) => a.cv === b.cv ? a.arrival - b.arrival : a.cv - b.cv);
    this.bracket(() => {
      for (const p of releasable) this.deliver(p.qid, p.ev);
    });
  }
  /** Deliver one event, preceded by this query's held `hello` if it has one — so the view's
   *  reset and its refill happen inside the same bracket and no listener sees the gap. */
  deliver(qid, ev) {
    const hello = this.heldHello.get(qid);
    if (hello) {
      this.heldHello.delete(qid);
      this.emitServerEvent(qid, hello);
    }
    if (ev.type === "snapshot") {
      this.snapshotOwed.delete(qid);
      this.hydrated.add(qid);
      this.resultType?.(qid, "complete");
    }
    this.emitServerEvent(qid, ev);
  }
  /** Run `body` inside one commit bracket, so every view it touches folds before any listener
   *  runs. A backend whose Store predates `onCommitBoundary` just runs the body. */
  bracket(body) {
    if (!this.commitBoundary) {
      body();
      return;
    }
    this.commitBoundary("begin");
    try {
      body();
    } finally {
      this.commitBoundary("end");
    }
  }
  /** Drop the gate's state. The frames it held were sorted against a watermark that no longer
   *  applies; each query re-subscribes and re-hydrates from scratch.
   *
   *  And ARMS the hold ({@link armHold}) rather than clearing it, because every caller resets for
   *  the same reason: the queries that will come back are about to re-hydrate. A daemon restart
   *  reaches this through
   *  `onRestart`, which fires on the first hello of the new process — AFTER `onResync` armed the
   *  hold on the reconnect. Clearing it there let the first query back release on its own while
   *  the rest were still re-subscribing, showing one query post-restart beside another pre-: the
   *  tear the hold exists to prevent (426 §9.2). */
  reset() {
    this.buffer = [];
    this.appliedCv = 0;
    this.pendingCvMin = 0;
    this.heldHello.clear();
    this.snapshotOwed.clear();
    this.armHold();
  }
  /** Arm the snapshot-owed hold for every query about to re-hydrate WITH state to lose — the one
   *  place the hold's membership rule lives, reached by both `onResync` and `reset()`. Nothing to
   *  arm when frames apply on arrival: there is no release to hold back. */
  armHold() {
    if (this.reveal !== "committed") return;
    for (const qid of this.registered) if (this.hydrated.has(qid) && !this.lost.has(qid)) this.owe(qid);
  }
  /** Start owing a fresh snapshot. Unconditional: a query that owes one ALREADY buffered is
   *  re-armed, because whatever armed it again (a second `hello`, a resync mid-hold) discarded
   *  that snapshot along with the rest of its epoch. */
  owe(qid) {
    this.snapshotOwed.set(qid, null);
  }
  /** The source terminally gave up on a subscription: a refused lease, a non-retryable
   *  `queryError`. Nothing will re-subscribe it, so it must stop pinning the rest of the
   *  connection — and stay un-armed across every later resync and restart, since `registered`
   *  outlives the source's subscription set (the app still holds the view, and is expected to:
   *  it was handed an error, not a teardown). */
  dropQuery(qid) {
    if (!this.registered.has(qid)) return;
    this.lost.add(qid);
    this.forget(qid);
  }
  /** Drop everything the gate holds on one query's behalf, and release what that was pinning.
   *  Nothing else will say so: a closed or refused view produces no frame, and on a quiet
   *  connection no other frame is coming. */
  forget(qid) {
    this.heldHello.delete(qid);
    this.buffer = this.buffer.filter((p) => p.qid !== qid);
    if (this.snapshotOwed.delete(qid)) this.tryRelease();
  }
  rehydrateAll() {
    this.reset();
    this.source.resubscribeAll?.();
  }
  emitServerEvent(qid, ev) {
    this.handler(qid, ev);
    if (this.devObservers.size) {
      for (const o of this.devObservers) o.onServerDelta?.(qid, { format: "flat", event: ev });
    }
  }
};
function createFlatStore(schema, source, opts = {}) {
  return new Store(schema, new FlatBackend(source, opts));
}

// src/live.ts
function leaser(fn, signal) {
  let cached;
  let leasing;
  return {
    /** Drop the cache: the socket closed, so the next subscribe must present a fresh lease. */
    forget() {
      cached = void 0;
    },
    async get() {
      if (cached && cached.expiresAt > Date.now() + 5e3) return cached;
      leasing ??= fn(signal);
      try {
        const minted = await leasing;
        cached = { leaseToken: minted.leaseToken, expiresAt: Date.now() + minted.expiresInMs };
        return cached;
      } finally {
        leasing = void 0;
      }
    }
  };
}
function openConnection(options) {
  const lifetime = new AbortController();
  const leasers = /* @__PURE__ */ new Map();
  const members = /* @__PURE__ */ new Map();
  let closed = false;
  let sequence = 0;
  const upgradeLease = async () => {
    let last;
    for (const held of leasers.values()) {
      try {
        return await held.get();
      } catch (failure) {
        last = failure;
      }
    }
    throw last ?? new Error("This connection has no live query to authorize its socket.");
  };
  const transport = new WsTransport(async () => options.socketUrl((await upgradeLease()).leaseToken), {
    onState: (state) => {
      if (closed) return;
      if (state === "closed") for (const held of leasers.values()) held.forget();
      for (const member of [...members.values()]) member.onTransportState(state);
    },
    onConnectError: (failure) => {
      const terminal = ![408, 425, 429, 500, 502, 503, 504].includes(failure?.status ?? 503);
      for (const member of [...members.values()]) member.fail(failure, terminal);
    }
  });
  const source = new RemoteFlatSource(transport, {
    // Per query, by the identity `attach` minted. The session passes the subscription's own
    // `remote` on every subscribe — first open, reconnect, gap, epoch drift — so this is also the
    // re-lease path, and it re-leases the AST the subscription was registered under.
    resolveSubscribe: async ({ remote }) => {
      const held = leasers.get(remote?.name);
      if (!held) throw new Error("No lease for this query; it was closed.");
      return { leaseToken: (await held.get()).leaseToken };
    },
    onQueryError: (remote, failure) => {
      const member = members.get(remote?.name);
      const error = Object.assign(new Error(failure.message), { status: failure.status });
      if (member) member.fail(error, !failure.retryable);
      else for (const other of [...members.values()]) other.fail(error, !failure.retryable);
    }
  });
  const store = createFlatStore(options.schema, source, { reveal: "immediate" });
  const close = () => {
    if (closed) return;
    closed = true;
    for (const member of [...members.values()]) member.dispose();
    members.clear();
    leasers.clear();
    lifetime.abort();
    source.close();
    transport.close();
  };
  async function attach(attachOptions) {
    if (closed) throw new Error("This connection is closed.");
    attachOptions.signal?.throwIfAborted();
    const name = `r2db.live.${++sequence}`;
    const held = leaser(attachOptions.lease, lifetime.signal);
    leasers.set(name, held);
    let status = "connecting";
    let error = null;
    let done = false;
    let initial = true;
    let awaitingSnapshot = true;
    let data = attachOptions.ast.one ? null : [];
    const listeners = /* @__PURE__ */ new Set();
    const errors = /* @__PURE__ */ new Set();
    const emit = () => {
      for (const listener of listeners) {
        try {
          listener(data);
        } catch (failure) {
          console.error("r2db live listener failed", failure);
        }
      }
    };
    let resolveReady;
    let rejectReady;
    const ready = new Promise((resolve, reject) => {
      resolveReady = resolve;
      rejectReady = reject;
    });
    let timeout;
    let detachChanges;
    let detachView;
    const dispose = () => {
      if (done) return;
      done = true;
      if (timeout !== void 0) clearTimeout(timeout);
      attachOptions.signal?.removeEventListener("abort", abort);
      detachChanges?.();
      detachView?.();
      view?.destroy();
      leasers.delete(name);
      members.delete(name);
    };
    const fail = (failure, terminal = true) => {
      if (done) return;
      error = failure instanceof Error ? failure : new Error(String(failure));
      status = terminal ? "error" : "reconnecting";
      emit();
      for (const listener of errors) {
        try {
          listener(error);
        } catch (other) {
          console.error("r2db live error listener failed", other);
        }
      }
      if (terminal) {
        rejectReady(error);
        dispose();
      }
    };
    const abort = () => fail(attachOptions.signal?.reason ?? new Error("Subscription canceled."));
    let view;
    members.set(name, {
      name,
      onTransportState: (state) => {
        if (done || state === "open") return;
        if (state === "closed") awaitingSnapshot = true;
        status = initial ? "connecting" : "reconnecting";
        emit();
      },
      fail,
      dispose
    });
    const query = { ast: () => attachOptions.ast, name, args: {} };
    view = store.materialize(query);
    detachView = view.subscribe(() => {
      if (done || awaitingSnapshot) return;
      data = view.data;
      emit();
    });
    detachChanges = store.subscribeChanges((qid, event) => {
      if (qid !== view.qid) return;
      if (event.type === "hello") awaitingSnapshot = true;
      if (event.type !== "snapshot" || !event.last) return;
      queueMicrotask(() => {
        if (done) return;
        data = view.data;
        awaitingSnapshot = false;
        initial = false;
        status = "live";
        error = null;
        if (timeout !== void 0) clearTimeout(timeout);
        resolveReady();
        emit();
      });
    });
    timeout = setTimeout(() => fail(new Error("Timed out waiting for the initial live query snapshot.")), attachOptions.timeoutMs ?? 3e4);
    attachOptions.signal?.addEventListener("abort", abort, { once: true });
    if (attachOptions.signal?.aborted) abort();
    try {
      await ready;
    } catch (failure) {
      dispose();
      throw failure;
    }
    return {
      get data() {
        return data;
      },
      get status() {
        return status;
      },
      get error() {
        return error;
      },
      subscribe(listener) {
        listeners.add(listener);
        listener(data);
        return () => listeners.delete(listener);
      },
      onError(listener) {
        errors.add(listener);
        if (error) listener(error);
        return () => errors.delete(listener);
      },
      close() {
        if (!done) {
          status = "closed";
          emit();
          dispose();
        }
        listeners.clear();
        errors.clear();
      }
    };
  }
  return { attach, close };
}
async function openLive(options) {
  const connection = openConnection({ schema: options.schema, socketUrl: options.socketUrl });
  let result;
  try {
    result = await connection.attach({
      ast: options.ast,
      lease: options.lease,
      ...options.signal ? { signal: options.signal } : {},
      ...options.timeoutMs !== void 0 ? { timeoutMs: options.timeoutMs } : {}
    });
  } catch (failure) {
    connection.close();
    throw failure;
  }
  return {
    ...result,
    get data() {
      return result.data;
    },
    get status() {
      return result.status;
    },
    get error() {
      return result.error;
    },
    subscribe: (listener) => result.subscribe(listener),
    onError: (listener) => result.onError(listener),
    // The connection exists for this result alone, so closing the result closes the socket.
    close() {
      result.close();
      connection.close();
    }
  };
}

// src/browser.ts
async function subscribe(query, options) {
  options.signal?.throwIfAborted();
  const ast = typeof query.ast === "function" ? query.ast() : query;
  const urls = /* @__PURE__ */ new Map();
  return openLive({
    schema: options.schema,
    ast,
    signal: options.signal,
    timeoutMs: options.timeoutMs,
    lease: async (signal) => {
      const minted = await options.lease(signal);
      if (!minted || typeof minted.leaseToken !== "string" || typeof minted.socketUrl !== "string") {
        throw new TypeError("lease() must resolve to { leaseToken, expiresInMs, socketUrl }.");
      }
      urls.clear();
      urls.set(minted.leaseToken, minted.socketUrl);
      return minted;
    },
    socketUrl: (token) => {
      const url = urls.get(token);
      if (!url) throw new Error("No socket URL for this lease.");
      return url;
    }
  });
}
function connect(options) {
  const urls = /* @__PURE__ */ new Map();
  const connection = openConnection({
    schema: options.schema,
    socketUrl: (token) => {
      const url = urls.get(token);
      if (!url) throw new Error("No socket URL for this lease.");
      return url;
    }
  });
  return {
    subscribe(query, subscribeOptions) {
      subscribeOptions.signal?.throwIfAborted();
      const ast = typeof query.ast === "function" ? query.ast() : query;
      return connection.attach({
        ast,
        lease: async (signal) => {
          const minted = await subscribeOptions.lease(signal);
          if (!minted || typeof minted.leaseToken !== "string" || typeof minted.socketUrl !== "string") {
            throw new TypeError("lease() must resolve to { leaseToken, expiresInMs, socketUrl }.");
          }
          urls.set(minted.leaseToken, minted.socketUrl);
          return minted;
        },
        ...subscribeOptions.signal ? { signal: subscribeOptions.signal } : {},
        ...subscribeOptions.timeoutMs !== void 0 ? { timeoutMs: subscribeOptions.timeoutMs } : {}
      });
    },
    close() {
      connection.close();
      urls.clear();
    }
  };
}
export {
  connect,
  newQueryBuilder,
  subscribe
};
